/**
 * CreateLookModal — Créer un look à partir du dressing
 *
 * Flow :
 *  1. Sélection multiple d'articles du dressing
 *  2. Choix d'une occasion (Casual / Soirée / Bureau / Sport / Cérémonie / Week-end)
 *  3. Si >= 2 articles → valider et afficher suggestions IA pour compléter
 *  4. Si 1 seul article → générer un look complet autour via IA
 *
 * Props :
 *  visible       : bool
 *  onClose       : () => void
 *  wardrobe      : array d'articles du dressing
 *  catalog       : array du catalogue pour suggestions
 *  onLookCreated : (look) => void — callback après validation finale
 */
import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import {
  View, Text, TouchableOpacity, ScrollView, Image, StyleSheet, ActivityIndicator, Alert, TextInput,
  Platform, Dimensions,
} from 'react-native';
import AppModal from './AppModal';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { colors, spacing, radius, shadow } from '../theme';
import { detectMissingPieces, findCatalogSuggestions, getOutfitCatalogSuggestions } from '../services/AIStylistService';
import { suggestCatalogItems } from '../services/OutfitMatchingService';
import { fetchCompleteLook } from '../services/LookCompletionApiService';
import { fetchScoreLook } from '../services/LookScoreApiService';
import { exhaustiveSearch } from '../services/SearchAgentService';
import { getFavorites } from '../services/FavoritesService';
import { hasUsableImage } from '../utils/imageFilter';
// v1.13.44 — masquer les images mortes (404) aussi dans "créer un look"
import { useDeadImagesVersion } from '../utils/deadImages';
import { useI18n } from '../i18n';
import {
  calculateLookScore,
  calculateCompatibility,
  generateLookNames,
  validateLookName,
  extractStrengthsWeaknesses,
} from '../services/LookBuilderScorer';

// SmartImage inline : fallback transparent si pas d'image
// v1.12.899 — Migration safeExpoImage (cache disque + recyclage views)
// + fallback automatique RN Image si module natif manquant (OTA-safe)
const { Image: SafeImage } = require('../utils/safeExpoImage');
function SmartImage({ uri, style, resizeMode = 'contain' }) {
  const [fallback, setFallback] = useState(false);
  // v1.12.510 — Force HTTPS (catalogue TOUS retourne http://static.tous.com
  // → bloqué par Mixed Content sur web HTTPS). Tous les CDN modernes supportent HTTPS.
  const safeUri = uri && typeof uri === 'string' && uri.startsWith('http://')
    ? 'https://' + uri.slice(7)
    : uri;
  if (!safeUri || fallback) {
    return (
      <View style={[style, { backgroundColor: '#F5F5F5', alignItems: 'center', justifyContent: 'center' }]}>
        <Text style={{ fontSize: 24, opacity: 0.3 }}>👗</Text>
      </View>
    );
  }
  const webProps = Platform.OS === 'web' ? { loading: 'eager', decoding: 'async' } : {};
  const finalStyle = Platform.OS === 'web'
    ? [style, { objectFit: resizeMode, backgroundColor: '#FAFAFA' }]
    : [style, { backgroundColor: '#FAFAFA' }];
  return (
    <SafeImage
      source={{ uri: safeUri }}
      style={finalStyle}
      contentFit={resizeMode}
      cachePolicy="memory-disk"
      transition={150}
      recyclingKey={safeUri}
      onError={() => setFallback(true)}
      {...webProps}
    />
  );
}

// Map canonical category values (FR keys stored in DB) → i18n keys
const CAT_LABEL_KEY = {
  Hauts: 'cat_tops', Bas: 'cat_bottoms', Robes: 'cat_dresses',
  Vestes: 'cat_jackets', Manteaux: 'cat_coats', Chaussures: 'cat_shoes',
  Sacs: 'cat_bags', Accessoires: 'cat_accessories', Bijoux: 'cat_jewelry',
  Maillots: 'cat_swimwear',
};
function translateCat(cat, t) {
  if (!cat) return '';
  const key = CAT_LABEL_KEY[cat];
  return key ? t(key) : cat;
}

// Canonical occasion ids (used as keys for matching/scoring) + i18n keys for UI labels
const OCCASIONS = [
  { id: 'Casual',    icon: '👕', labelKey: 'occ_casual',   descKey: 'occ_casual_desc' },
  { id: 'Soirée',    icon: '✨', labelKey: 'occ_evening',  descKey: 'occ_evening_desc' },
  { id: 'Bureau',    icon: '💼', labelKey: 'occ_work',     descKey: 'occ_work_desc' },
  { id: 'Sport',     icon: '⚽', labelKey: 'occ_sport',    descKey: 'occ_sport_desc' },
  { id: 'Cérémonie', icon: '💎', labelKey: 'occ_ceremony', descKey: 'occ_ceremony_desc' },
  { id: 'Week-end',  icon: '🌿', labelKey: 'occ_weekend',  descKey: 'occ_weekend_desc' },
  { id: 'Voyage',    icon: '✈️', labelKey: 'occ_travel',   descKey: 'occ_travel_desc' },
  { id: 'Rendez-vous', icon: '💕', labelKey: 'occ_date',  descKey: 'occ_date_desc' },
];

// v1.12.903 — GridCard memoized : évite re-render quand selected/wardrobe change
const GridCard = React.memo(function GridCard({ item, isSel, onPress, onLongPress, translateCat, t, s }) {
  const ps = item.ai_metadata?.pseudo;
  const isCat = item._source === 'catalog';
  return (
    <TouchableOpacity
      style={[s.gridCard, isSel && s.gridCardSelected]}
      onPress={onPress}
      onLongPress={onLongPress}
      delayLongPress={280}
      activeOpacity={0.85}
    >
      <SmartImage
        uri={item.image_url || item.image}
        style={s.gridImg}
      />
      {/* v1.13.27 — signal "appui long = agrandir l'image" */}
      <View style={s.zoomHintBadge} pointerEvents="none">
        <Text style={s.zoomHintIcon}>⤢</Text>
      </View>
      <View style={[s.sourceBadge, isCat && s.sourceBadgeCatalog]}>
        <Text style={s.sourceBadgeText}>{isCat ? '🛍️' : '👗'}</Text>
      </View>
      {isSel && (
        <View style={s.checkBadge}>
          <Text style={s.checkBadgeText}>✓</Text>
        </View>
      )}
      <Text style={s.gridName} numberOfLines={1}>
        {isCat ? (item.brand || item.name) : (ps || item.brand || item.name)}
      </Text>
      <Text style={s.gridCat}>
        {translateCat(item.category, t)}
        {isCat && item.price ? ' · ' + (item.price || 0).toFixed(0) + '€' : ''}
      </Text>
    </TouchableOpacity>
  );
}, (prev, next) => prev.item.id === next.item.id && prev.isSel === next.isSel);

export default function CreateLookModal({ visible, onClose, wardrobe: wardrobeProp = [], catalog = [], gender = 'F', onLookCreated, onProductPress, onTryOnLook, onAddToCalendar, onShareLook, onViewMyLooks, onRequestSignIn, onRequestSubscribe, onAddToWardrobe, isAuthenticated = true, canTryOn = true, existingLookNames = [], preselectedItemId = null }) {
  // v1.12.580 — V5.1-bis : exclure les items _hidden (au-delà du quota tier + bonus)
  // L'user doit upgrade / acheter Boost / abonnement wardrobe pour les utiliser.
  const wardrobe = React.useMemo(() => (wardrobeProp || []).filter(it => !it._hidden), [wardrobeProp]);
  const insets = useSafeAreaInsets();
  const { t, lang } = useI18n();
  const [step, setStep]         = useState('select');    // 'select' | 'occasion' | 'preview' | 'score'
  // v1.12.271 — UX simplifiée : sélection toujours cumulée dressing+catalogue (sourceMode='mix' interne)
  // L'utilisateur navigue via 2 onglets (Dressing/Catalogue) qui filtrent l'affichage de la grille.
  const sourceMode = 'mix';
  // v1.13.x — Onglet Catalogue par défaut (découverte des articles dès l'ouverture).
  const [activeTab, setActiveTab] = useState('catalog'); // 'wardrobe' | 'catalog' | 'favorites'
  // v1.13.x — Long-press sur un article → aperçu de l'image en grand (overlay, pas un modal imbriqué)
  const [zoomItem, setZoomItem] = useState(null);
  // v1.12.291 — favoris dans Create Look
  const [favorites, setFavorites] = useState([]);
  React.useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    // v1.12.894 — GA4 : ouverture de Créer un look
    try {
      const { trackEvent } = require('../services/AnalyticsService');
      trackEvent('create_look_opened', {
        wardrobe_count: Array.isArray(wardrobeProp) ? wardrobeProp.length : 0,
        catalog_count: Array.isArray(catalog) ? catalog.length : 0,
        gender,
        preselected: !!preselectedItemId,
      });
    } catch {}
    getFavorites().then(list => {
      if (!cancelled) setFavorites(Array.isArray(list) ? list : []);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [visible]);
  const [selected, setSelected] = useState([]);          // ids d'articles sélectionnés (mix dressing + catalogue)
  // v1.12.272 — Suggestions IA cliquées depuis l'écran de propositions (s'ajoutent au look)
  // On stocke l'objet entier car les suggestions ne sont PAS dans le pool wardrobe/catalog d'origine.
  const [addedSuggestions, setAddedSuggestions] = useState([]); // [{id, ...item, _source:'catalog'}]
  // v1.12.501/520 — Pagination backend "Compléter mon look" : limit par catégorie
  // Réduit à 6 (au lieu de 12) pour éviter crash iOS RCTImageManager (trop d'images simultanées)
  const [backendCatLimit, setBackendCatLimit] = useState(6);
  const [loadingMoreCat, setLoadingMoreCat] = useState(null); // catName en cours de loading
  // v1.12.520 — Si on revient à 0 items sélectionnés DEPUIS preview/score :
  // forcer le retour à select SANS reset agressif de suggestions (qui démonte
  // trop d'images d'un coup et crash RCTImageManager iOS).
  // Les suggestions sont gardées en mémoire ; generateSuggestions les remplace
  // proprement au prochain clic. Reset uniquement quand modal se ferme (reset()).
  useEffect(() => {
    if (selected.length === 0 && (step === 'preview' || step === 'score')) {
      setStep('select');
    }
  }, [selected.length]);

  // v1.12.503/505 — Ref pour scroller au top du modal à chaque changement de step
  // Délai 0ms pour laisser React rendre le nouveau step avant de scroller (sinon RN-web ignore)
  const mainScrollRef = useRef(null);
  useEffect(() => {
    const scrollTop = () => {
      if (!mainScrollRef.current) return;
      try { mainScrollRef.current.scrollTo({ x: 0, y: 0, animated: false }); } catch {}
      // Fallback web : scroll natif sur l'élément DOM
      if (Platform.OS === 'web' && mainScrollRef.current?.scrollTop !== undefined) {
        mainScrollRef.current.scrollTop = 0;
      }
    };
    const id1 = setTimeout(scrollTop, 0);
    const id2 = setTimeout(scrollTop, 50);
    return () => { clearTimeout(id1); clearTimeout(id2); };
  }, [step]);
  // ═══ Option A v1.12.73 ═══
  const [lookScore, setLookScore]     = useState(null);   // résultat calculateLookScore
  const [nameDialog, setNameDialog]   = useState(false);  // affichage saisie nom
  const [lookName, setLookName]       = useState('');
  const [nameError, setNameError]     = useState(null);
  const [nameSuggestions, setNameSuggestions] = useState([]);
  const [savedLook, setSavedLook]     = useState(null);   // look enregistré (pour next_actions)
  const [showNextActions, setShowNextActions] = useState(false);
  const [calendarDone, setCalendarDone] = useState(false);
  // v1.12.219 — recherche + filtre catégorie dans le dressing
  const [searchQ, setSearchQ]       = useState('');
  // v1.12.537 — Debounce search : évite crash iOS RCTImageManager (remount massif des Image
  // à chaque keystroke). 250ms = bon équilibre réactivité/perf.
  const [searchQDebounced, setSearchQDebounced] = useState('');
  React.useEffect(() => {
    const id = setTimeout(() => setSearchQDebounced(searchQ), 250);
    return () => clearTimeout(id);
  }, [searchQ]);
  const [filterCat, setFilterCat]   = useState('Tout');
  const [filterPartner, setFilterPartner] = useState('Tous'); // v1.12.312 — filtre partenaire (onglet catalogue)
  // v1.12.456 — pagination scroll dans le catalogue : 60 items initial,
  // +60 à chaque scroll proche du bas. Reset à 60 quand filtre/onglet change.
  // v1.12.903 — Perf scroll Android : page 60→24 (initial render plus rapide).
  // Le scroll-to-bottom infini charge progressivement (+24 par batch).
  const CATALOG_PAGE_SIZE = 24;
  const [displayedCount, setDisplayedCount] = useState(CATALOG_PAGE_SIZE);
  React.useEffect(() => {
    setDisplayedCount(CATALOG_PAGE_SIZE);
  }, [filterCat, filterPartner, activeTab]);
  // v1.12.460 — Debug log déplacé APRÈS gridItems (cf. plus bas pour éviter TDZ)
  const [occasion, setOccasion] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [groupedSuggestions, setGroupedSuggestions] = useState([]); // [{ category, reason, priority, items }]
  const [loading, setLoading]   = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [extraCatalog, setExtraCatalog] = useState([]); // articles récupérés via loadMore
  const [richCatalog, setRichCatalog] = useState([]);   // catalogue dynamique complet (chargé à l'ouverture)
  const [loadingCatalog, setLoadingCatalog] = useState(false);
  const rotationRef = React.useRef(0);

  // ═══ Pré-sélection : si un article vient du Dressing (bouton "Créer un look" du bottom sheet) ═══
  // On le met direct dans la sélection + on ouvre sur l'étape 'select' pour pouvoir ajouter d'autres
  React.useEffect(() => {
    if (visible && preselectedItemId) {
      setSelected([preselectedItemId]);
      setStep('select'); // Laisse l'user voir et ajouter d'autres articles
    } else if (visible && !preselectedItemId) {
      // Ouverture classique sans pré-sélection
      setSelected([]);
      setStep('select');
    }
  }, [visible, preselectedItemId]);

  // ═══ Au premier affichage, charger le catalogue complet ═══
  // Même stratégie que LooksScreen : dynamique + partenaires
  React.useEffect(() => {
    if (!visible || richCatalog.length > 0) return;

    // ═══ DISPLAY IMMÉDIAT : utiliser le catalog externe comme pool initial ═══
    // Le WardrobeScreen passe partnerProducts qui est déjà chargé avec cache.
    // Évite d'attendre le re-fetch complet avant d'afficher quelque chose.
    // v1.12.464 — filtre 4 partenaires à la SOURCE aussi sur le pool instantané.
    const _allowedSlugs = new Set(['ann_demeulemeester', 'suit_negozi', 'coutr', 'tous', 'drestige', 'tamaris', 'desigual']);
    const _allowedBrandKw = ['ann demeulemeester', 'suit negozi', 'coutr', 'tous', 'drestige', 'tamaris', 'desigual'];
    const _isAllowedInstant = (p) => {
      const pp = String(p?.partner || '').trim().toLowerCase();
      if (pp && _allowedSlugs.has(pp)) return true;
      const bb = String(p?.brand || '').trim().toLowerCase();
      return _allowedBrandKw.some((kw) => bb === kw || bb.includes(kw));
    };
    if (catalog && catalog.length > 100) {
      let quickValid = catalog.filter(p => {
        if (!p || !p.id) return false;
        if (!_isAllowedInstant(p)) return false;
        const img = p.image || p.image_url;
        if (!img || typeof img !== 'string' || !/^https?:\/\//.test(img)) return false;
        const link = p.affiliateUrl || p.originalUrl || p.url || p.link;
        if (!link || typeof link !== 'string' || !/^https?:\/\//.test(link)) return false;
        const priceNum = parseFloat(p.price);
        return !isNaN(priceNum) && priceNum > 0;
      });
      // ═══ FILTRE GENRE STRICT sur le pool instantané ═══
      try {
        const GF = require('../services/GenderFilter');
        if (GF && GF.filterByGender) quickValid = GF.filterByGender(quickValid, gender);
      } catch(e) {}
      if (quickValid.length > 0) {
        console.log(`[CreateLook] instant pool from external: ${quickValid.length} (4 partners only)`);
        setRichCatalog(quickValid);
      }
    }

    setLoadingCatalog(true);

    // v1.12.279 — LOAD PROGRESSIF : chaque source append au pool dès qu'elle arrive
    // Avantage : l'user voit des articles instantanément (dynamic catalog en premier — cache 24h)
    // puis le pool s'enrichit au fur et à mesure. Pas de wait sur Promise.allSettled.
    const isCancelled = { v: false };

    const validateAndDedup = (raw, currentPool) => {
      const seenIds = new Set(currentPool.map(p => p.id));
      const out = [];
      for (const p of raw) {
        if (!p || !p.id || seenIds.has(p.id)) continue;
        const img = p.image || p.image_url;
        if (!img || typeof img !== 'string' || !/^https?:\/\//.test(img)) continue;
        const link = p.affiliateUrl || p.originalUrl || p.url || p.link;
        if (!link || typeof link !== 'string' || !/^https?:\/\//.test(link)) continue;
        const priceNum = parseFloat(p.price);
        if (isNaN(priceNum) || priceNum <= 0) continue;
        seenIds.add(p.id);
        out.push(p);
      }
      // Filtre genre (regex rattrapage)
      try {
        const GF = require('../services/GenderFilter');
        let filtered = out;
        if (GF && GF.filterHautsByGenderStrict) filtered = GF.filterHautsByGenderStrict(filtered, gender);
        if (gender && ['M', 'F'].includes(gender) && GF && GF.filterByGender) filtered = GF.filterByGender(filtered, gender);
        return filtered;
      } catch(e) { return out; }
    };

    // v1.12.464 — filtre 4 partenaires officiels Mytenue à la SOURCE
    // (avant: iqueens/zalando rentraient dans catalogPool puis étaient filtrés
    // côté gridItems → gaspillage mémoire/réseau).
    const ALLOWED_PARTNER_SLUGS_LOAD = new Set(['ann_demeulemeester', 'suit_negozi', 'coutr', 'tous', 'drestige', 'tamaris', 'desigual']);
    const ALLOWED_BRAND_LOAD = ['ann demeulemeester', 'suit negozi', 'coutr', 'tous', 'drestige', 'tamaris', 'desigual'];
    const isAllowedPartnerLoad = (it) => {
      const p = String(it?.partner || '').trim().toLowerCase();
      if (p && ALLOWED_PARTNER_SLUGS_LOAD.has(p)) return true;
      const b = String(it?.brand || '').trim().toLowerCase();
      return ALLOWED_BRAND_LOAD.some((kw) => b === kw || b.includes(kw));
    };

    const appendSource = (newItems, sourceName) => {
      const inputCount = Array.isArray(newItems) ? newItems.length : 0;
      // v1.12.495 — logs détaillés pour tracer la provenance des items
      console.log(`[CreateLook/SRC] ${sourceName}: received ${inputCount} items from backend`);
      if (inputCount > 0 && inputCount <= 3) {
        // Sample : montre id+partner+brand des 3 premiers (debug)
        const sample = newItems.slice(0, 3).map(i => ({
          id: i?.id, partner: i?.partner, brand: i?.brand, name: (i?.name || '').slice(0, 40),
        }));
        console.log(`[CreateLook/SRC] ${sourceName} sample:`, sample);
      } else if (inputCount > 0) {
        const sample = newItems.slice(0, 2).map(i => ({
          id: i?.id, partner: i?.partner, brand: i?.brand,
        }));
        console.log(`[CreateLook/SRC] ${sourceName} first 2:`, sample);
      }
      if (isCancelled.v || !newItems || inputCount === 0) return;
      const allowed = newItems.filter(isAllowedPartnerLoad);
      const rejected = inputCount - allowed.length;
      if (allowed.length === 0) {
        console.log(`[CreateLook/SRC] ${sourceName}: ALL ${inputCount} rejected by partner filter (4-partner allowlist)`);
        return;
      }
      if (rejected > 0) {
        console.log(`[CreateLook/SRC] ${sourceName}: ${allowed.length} allowed / ${rejected} rejected`);
      }
      setRichCatalog(prev => {
        const added = validateAndDedup(allowed, prev);
        const dedupRejected = allowed.length - added.length;
        if (added.length === 0) {
          console.log(`[CreateLook/SRC] ${sourceName}: 0 added (all ${allowed.length} dedup-rejected — already in pool or invalid url/price)`);
          return prev;
        }
        if (dedupRejected > 0) {
          console.log(`[CreateLook/SRC] ${sourceName}: +${added.length} added · ${dedupRejected} dedup-rejected · pool=${prev.length + added.length}`);
        } else {
          console.log(`[CreateLook/SRC] ${sourceName}: +${added.length} added · pool=${prev.length + added.length}`);
        }
        return [...prev, ...added];
      });
    };

    // 1. Catalogue dynamique en PRIORITÉ (cache 24h → quasi-instantané) + partners en parallèle
    // v1.12.462 — ajout SUIT/ANN/COUTR (étaient absents) + retire Zalando/Asos/Zara/HM
    //   (filtrés côté isAllowedPartner de toute façon, économise les appels réseau)
    const { fetchDynamicCatalog } = require('../services/DynamicCatalogService');
    const { getCatalog }          = require('../services/DatabaseService');

    const sources = [
      [fetchDynamicCatalog(gender),                                                       'dynamic'],
      [getCatalog({ partner: 'suit_negozi',        limit: 1500, available: true, gender }), 'suit'],
      [getCatalog({ partner: 'ann_demeulemeester', limit: 500,  available: true, gender }), 'ann'],
      [getCatalog({ partner: 'coutr',              limit: 2000, available: true, gender }), 'coutr'],
      [getCatalog({ partner: 'drestige',           limit: 2000, available: true, gender }), 'drestige'],
      [getCatalog({ partner: 'tamaris',            limit: 2000, available: true, gender }), 'tamaris'],
      [getCatalog({ partner: 'desigual',           limit: 2000, available: true, gender }), 'desigual'],
    ];

    let pendingCount = sources.length;
    const markDone = () => {
      pendingCount -= 1;
      if (pendingCount <= 0 && !isCancelled.v) setLoadingCatalog(false);
    };

    // v1.12.495 — log état initial pour comparer aux résultats
    console.log(`[CreateLook/INIT] starting ${sources.length} sources fetch — gender=${gender}`);
    const _tStart = Date.now();
    for (const [promise, name] of sources) {
      promise
        .then(items => appendSource(items, name))
        .catch(e => console.warn(`[CreateLook/SRC] ${name} FAILED:`, e?.message || e))
        .finally(() => {
          markDone();
          if (pendingCount <= 0 && !isCancelled.v) {
            console.log(`[CreateLook/INIT] all sources done in ${Date.now() - _tStart}ms`);
          }
        });
    }

    return () => { isCancelled.v = true; };
  }, [visible, gender]);

  // v1.13.44 — version du registre d'images mortes → re-filtre le pool en direct
  const deadImagesVersion = useDeadImagesVersion();
  // v1.12.73+ — Pool d'items disponible selon le sourceMode (wardrobe / catalog / mix)
  // Chaque item est tagué avec _source pour le scorer (bonus +10 wardrobe) et l'UI (badge).
  const catalogPool = useMemo(() => {
    // Priorité : richCatalog (chargé via useEffect) > catalog (prop)
    const pool = richCatalog.length > 0 ? richCatalog : (catalog || []);
    // v1.13.26 — Masquer les partenaires exclus (Zalando/H&M/Mango).
    // v1.13.44 — hasUsableImage couvre aussi les images MORTES (404 confirmé) et
    // vides → un article sans visuel exploitable n'apparaît pas dans "créer un look".
    try {
      return pool.filter(it => hasUsableImage(it));
    } catch { return pool; }
  }, [richCatalog, catalog, deadImagesVersion]);

  // v1.12.271 — Pool complet (mix dressing+catalogue) pour la sélection cumulée
  // v1.12.272 — On y ajoute aussi les suggestions IA cliquées (addedSuggestions)
  // pour que selectedItems les inclue dans le bandeau "Mes articles sélectionnés".
  const displayedItems = useMemo(() => {
    const base = [
      ...(wardrobe || []).map(it => ({ ...it, _source: 'wardrobe' })),
      ...catalogPool.map(it => ({ ...it, _source: 'catalog' })),
    ];
    const baseIds = new Set(base.map(x => x.id));
    // v1.12.291 — inclure les favoris (taggés catalog) pour que la sélection les retrouve
    const favoritesExtra = (favorites || [])
      .filter(f => !baseIds.has(f.id))
      .map(f => ({ ...f, _source: 'catalog' }));
    favoritesExtra.forEach(f => baseIds.add(f.id));
    const extra = (addedSuggestions || []).filter(s => !baseIds.has(s.id));
    return [...base, ...favoritesExtra, ...extra];
  }, [wardrobe, catalogPool, favorites, addedSuggestions]);

  // Items affichés DANS LA GRILLE selon l'onglet actif
  // v1.12.291 — 3 sources : wardrobe | catalog | favorites
  // v1.12.453/454 — catalog/favorites filtrés sur les 4 partenaires officiels Mytenue.
  //   Match sur partner slug OU brand (les items n'ont pas tous partner rempli).
  const ALLOWED_PARTNER_SLUGS = new Set(['ann_demeulemeester', 'suit_negozi', 'coutr', 'tous', 'drestige', 'tamaris', 'desigual']);
  const ALLOWED_BRAND_KEYWORDS = ['ann demeulemeester', 'suit negozi', 'coutr', 'tous', 'drestige', 'tamaris', 'desigual'];
  const isAllowedPartner = (it) => {
    const p = String(it?.partner || '').trim().toLowerCase();
    if (p && ALLOWED_PARTNER_SLUGS.has(p)) return true;
    const b = String(it?.brand || '').trim().toLowerCase();
    return ALLOWED_BRAND_KEYWORDS.some((kw) => b === kw || b.includes(kw));
  };
  const gridItems = useMemo(() => {
    if (activeTab === 'wardrobe') {
      return (wardrobe || []).map(it => ({ ...it, _source: 'wardrobe' }));
    }
    if (activeTab === 'favorites') {
      // Les favoris sont des produits catalogue → on les tag _source='catalog' pour le scorer
      // v1.13.44 — exclure images mortes/vides
      return (favorites || []).filter(f => isAllowedPartner(f) && hasUsableImage(f)).map(it => ({ ...it, _source: 'catalog' }));
    }
    return catalogPool.filter(isAllowedPartner).map(it => ({ ...it, _source: 'catalog' }));
  }, [activeTab, wardrobe, catalogPool, favorites, deadImagesVersion]);

  // Sélection : on cherche les items sélectionnés DANS le pool complet (avec _source préservé)
  const selectedItems = useMemo(() =>
    displayedItems.filter(it => selected.includes(it.id)),
    [displayedItems, selected]
  );

  // v1.12.460 — Debug log catalogPool/gridItems (déplacé après les déclarations
  // pour éviter TDZ "Cannot access 'gridItems' before initialization")
  React.useEffect(() => {
    if (activeTab !== 'catalog' || !catalogPool?.length) return;
    const byPartner = new Map();
    const byBrand = new Map();
    const samples = [];
    for (const it of catalogPool) {
      const p = String(it?.partner || '<vide>').trim();
      const b = String(it?.brand || '<vide>').trim();
      byPartner.set(p, (byPartner.get(p) || 0) + 1);
      byBrand.set(b, (byBrand.get(b) || 0) + 1);
      if (samples.length < 3) samples.push({ id: it.id, partner: p, brand: b, name: it.name });
    }
    console.log('[CreateLookModal] catalogPool total =', catalogPool.length);
    console.log('[CreateLookModal] by partner =', Object.fromEntries(byPartner));
    console.log('[CreateLookModal] by brand   =', Object.fromEntries(byBrand));
    console.log('[CreateLookModal] samples    =', samples);
    console.log('[CreateLookModal] gridItems filtered =', gridItems.length);
  }, [activeTab, catalogPool, gridItems]);

  // v1.12.219 — Catégories disponibles (calculé sur gridItems pour refléter l'onglet)
  const availableCats = useMemo(() => {
    const set = new Set((gridItems || []).map(it => it.category).filter(Boolean));
    const ordered = ['Hauts', 'Bas', 'Robes', 'Maillots', 'Vestes', 'Manteaux', 'Chaussures', 'Sacs', 'Bijoux', 'Accessoires'];
    const present = ordered.filter(c => set.has(c));
    // ajouter les catégories non-listées en fin
    for (const c of set) if (!ordered.includes(c)) present.push(c);
    return ['Tout', ...present];
  }, [gridItems]);

  // v1.12.312 — Partenaires disponibles (onglet Catalogue uniquement)
  // v1.12.455 — liste FIXE des 4 partenaires officiels (slug + label affiché).
  //   On compte les items par partner slug pour ne montrer que ceux qui ont du stock.
  const PARTNER_CHIPS = [
    { slug: 'tous',              label: 'TOUS' },
    { slug: 'ann_demeulemeester', label: 'ANN' },
    { slug: 'suit_negozi',       label: 'SUIT' },
    { slug: 'coutr',             label: 'COUTR' },
    { slug: 'drestige',          label: 'DRESTIGE' },
    { slug: 'tamaris',           label: 'TAMARIS' },
    { slug: 'desigual',          label: 'DESIGUAL' },
  ];
  const matchesPartnerSlug = (it, slug) => {
    const p = String(it?.partner || '').trim().toLowerCase();
    if (p === slug) return true;
    // Fallback brand contains (si partner slug manquant)
    const b = String(it?.brand || '').trim().toLowerCase();
    const map = {
      tous: 'tous',
      ann_demeulemeester: 'ann demeulemeester',
      suit_negozi: 'suit negozi',
      coutr: 'coutr',
      drestige: 'drestige',
      tamaris: 'tamaris',
      desigual: 'desigual',
    };
    return b === map[slug] || b.includes(map[slug]);
  };
  const availablePartners = useMemo(() => {
    if (activeTab !== 'catalog') return [];
    const present = PARTNER_CHIPS.filter(({ slug }) =>
      (gridItems || []).some((it) => matchesPartnerSlug(it, slug)),
    );
    return ['Tous', ...present.map((p) => p.label)];
  }, [gridItems, activeTab]);

  // v1.12.219 — Items filtrés par recherche (nom + pseudo + marque) + catégorie
  // v1.12.274 — pour l'onglet Catalogue : utilise exhaustiveSearch (multi-champs FR/EN + synonymes)
  //             pour matcher la même UX que la page Catalogue principale
  // v1.12.278 — CAP catalogue à 200 items par défaut pour éviter crash Safari iOS
  //             (le catalogue peut contenir 4000+ items → memory crash en rendant tout d'un coup).
  //             Le cap saute dès qu'une recherche est active.
  // v1.12.279 — 60 = safe Safari iOS (mémoire ~150MB max sur iPhone). Au-delà : crash JS context.
  // v1.12.456 — remplacé par displayedCount state (pagination scroll bottom)
  const filteredItems = useMemo(() => {
    // v1.12.537 — Utilise searchQDebounced (250ms) au lieu de searchQ pour éviter
    // crash iOS RCTImageManager (remount d'Image massif à chaque keystroke)
    const q = (searchQDebounced || '').trim();
    // 1. Filtre catégorie d'abord (rapide)
    let base = (gridItems || []).filter(item => filterCat === 'Tout' || item.category === filterCat);
    // v1.12.312/455 — Filtre partenaire par slug (label → slug via PARTNER_CHIPS)
    if (activeTab === 'catalog' && filterPartner !== 'Tous') {
      const chip = PARTNER_CHIPS.find((p) => p.label === filterPartner);
      if (chip) base = base.filter(item => matchesPartnerSlug(item, chip.slug));
    }
    if (!q) {
      // Pas de recherche : pagination dynamique (60 + 60 par scroll bottom)
      if (activeTab === 'catalog' && base.length > displayedCount) {
        return base.slice(0, displayedCount);
      }
      return base;
    }
    // 2. Recherche — limite à 120 résultats pour éviter crash mémoire iOS sur grosse recherche
    if (activeTab === 'catalog' && q.length >= 2) {
      const results = exhaustiveSearch(q, base, { gender: gender || undefined, minScore: 20, limit: 120 });
      return results;
    }
    // Dressing : simple includes (set restreint, pas de synonymes nécessaires)
    const lq = q.toLowerCase();
    return base.filter(item => {
      const nm = (item.name || '').toLowerCase();
      const br = (item.brand || '').toLowerCase();
      const ps = (item.ai_metadata?.pseudo || '').toLowerCase();
      return nm.includes(lq) || br.includes(lq) || ps.includes(lq);
    });
  }, [gridItems, searchQDebounced, filterCat, filterPartner, activeTab, gender, displayedCount]);

  const toggleItem = (id) => {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  // v1.12.273 — suppression universelle (gère wardrobe + catalog + suggestions IA ajoutées)
  const removeItem = (id) => {
    setSelected(prev => prev.filter(x => x !== id));
    setAddedSuggestions(prev => prev.filter(x => x.id !== id));
  };

  const reset = () => {
    setStep('select');
    setSelected([]);
    setAddedSuggestions([]);
    setActiveTab('wardrobe');
    setOccasion(null);
    setSuggestions([]);
    setGroupedSuggestions([]);
    setBackendCatLimit(6);
    setLoadingMoreCat(null);
    setLookScore(null);
    setNameDialog(false);
    setLookName('');
    setNameError(null);
    setNameSuggestions([]);
    setSavedLook(null);
    setShowNextActions(false);
    setCalendarDone(false);
  };

  // v1.12.271 — changement d'onglet (Dressing/Catalogue) : on PRÉSERVE la sélection
  // (mix dressing + catalogue), on reset juste les filtres affichage.
  const handleTabChange = (tab) => {
    if (tab === activeTab) return;
    setActiveTab(tab);
    setFilterCat('Tout');
    setFilterPartner('Tous');
    setSearchQ('');
  };

  // ═══ Détecte la saison actuelle (hémisphère nord par défaut) ═══
  const getCurrentSeason = () => {
    const m = new Date().getMonth(); // 0-11
    if (m >= 2 && m <= 4)  return 'printemps';
    if (m >= 5 && m <= 7)  return 'été';
    if (m >= 8 && m <= 10) return 'automne';
    return 'hiver';
  };

  // ═══ Compute look score (mémoized) ═══
  // v1.12.509 — Sécurité : ne calcule que si on entre dans l'étape 'score'
  // Backend prioritaire (banks appliquées + diagnose précis), fallback local.
  const computeScore = async () => {
    const lookItems = selectedItems.map(it => ({
      ...it,
      _source: it._source || (it.affiliateUrl || it.link || it.url ? 'catalog' : 'wardrobe'),
    }));
    const ctx = { occasion, season: getCurrentSeason(), tempC: 18 };

    // v1.12.498 — Map occasion FR maj → canonical lowercase backend
    const OCCASION_TO_BACKEND = {
      'Casual': 'casual', 'Soirée': 'soiree', 'Bureau': 'travail',
      'Sport': 'sport', 'Cérémonie': 'ceremonie', 'Week-end': 'brunch',
      'Voyage': 'voyage', 'Rendez-vous': 'date',
    };
    const backendOccasion = OCCASION_TO_BACKEND[occasion] || 'casual';

    // ═══ Backend prioritaire (3 banks + computeCohesion + diagnose) ═══
    try {
      const _t0 = Date.now();
      console.log('[ScoreBackend] REQUEST →', {
        itemCount: lookItems.length, occasion: backendOccasion, gender,
      });
      const res = await fetchScoreLook({
        selectedItems: lookItems,
        context: { occasion: backendOccasion, tempC: 18 },
        gender: gender || 'F',
      });
      console.log('[ScoreBackend] RESPONSE ←', {
        ms: Date.now() - _t0,
        score: res.score, grade: res.grade?.labelKey,
        banksApplied: res.banksApplied,
        issuesCount: res.issues?.length || 0,
        explanationsSample: {
          color: res.breakdown?.color?.explanation?.slice(0, 80),
          style: res.breakdown?.style?.explanation?.slice(0, 80),
          material: res.breakdown?.material?.explanation?.slice(0, 80),
        },
      });

      // Map backend → format UI existant (compat avec LookBuilderScorer)
      const breakdown = res.breakdown || {};
      const mappedBreakdown = {
        style: {
          score: breakdown.style?.score ?? 0, weight: breakdown.style?.weight ?? 0.22,
          icon: '🎯', labelKey: 'score_style_match',
          weighted: Math.round((breakdown.style?.score ?? 0) * (breakdown.style?.weight ?? 0.22)),
          explanation: lang === 'en' ? breakdown.style?.explanationEn : breakdown.style?.explanation,
          banksApplied: breakdown.style?.banksApplied,
        },
        color: {
          score: breakdown.color?.score ?? 0, weight: breakdown.color?.weight ?? 0.22,
          icon: '🎨', labelKey: 'score_color_harmony',
          weighted: Math.round((breakdown.color?.score ?? 0) * (breakdown.color?.weight ?? 0.22)),
          explanation: lang === 'en' ? breakdown.color?.explanationEn : breakdown.color?.explanation,
          banksApplied: breakdown.color?.banksApplied,
        },
        material: {
          score: breakdown.material?.score ?? 0, weight: breakdown.material?.weight ?? 0.22,
          icon: '🧵', labelKey: 'score_material_compat',
          weighted: Math.round((breakdown.material?.score ?? 0) * (breakdown.material?.weight ?? 0.22)),
          explanation: lang === 'en' ? breakdown.material?.explanationEn : breakdown.material?.explanation,
          banksApplied: breakdown.material?.banksApplied,
        },
        ai_vision: {
          score: res.aiVision?.score ?? 0, weight: 0,
          icon: '👁️', labelKey: 'score_ai_vision',
          weighted: 0,
          explanation: lang === 'en' ? res.aiVision?.explanationEn : res.aiVision?.explanation,
          banksApplied: false,
        },
        context: {
          score: Math.round(((breakdown.formality?.score ?? 0) * 0.5 + (breakdown.weather?.score ?? 0) * 0.5)),
          weight: (breakdown.formality?.weight ?? 0.19) + (breakdown.weather?.weight ?? 0.15),
          icon: '🌡️', labelKey: 'score_context_fit',
          weighted: Math.round((breakdown.formality?.score ?? 0) * (breakdown.formality?.weight ?? 0.19) + (breakdown.weather?.score ?? 0) * (breakdown.weather?.weight ?? 0.15)),
          explanation: lang === 'en'
            ? `${breakdown.formality?.explanationEn || ''} / ${breakdown.weather?.explanationEn || ''}`
            : `${breakdown.formality?.explanation || ''} / ${breakdown.weather?.explanation || ''}`,
          banksApplied: false,
        },
      };

      setLookScore({
        final_score: res.score,
        grade: res.grade,
        breakdown: mappedBreakdown,
        _backendIssues: res.issues || [],
        _backendBanks: res.banksApplied || {},
        _backendContext: res.context || {},
        _fromBackend: true,
      });

      const names = generateLookNames(lookItems, ctx, lang || 'fr');
      setNameSuggestions(names);
      return;
    } catch (e) {
      console.warn('[ScoreBackend] FAILED → fallback local:', e?.message || e);
    }

    // ═══ Fallback : scorer local ═══
    try {
      const result = calculateLookScore(lookItems, ctx);
      setLookScore({ ...result, _fromBackend: false });
      const names = generateLookNames(lookItems, ctx, lang || 'fr');
      setNameSuggestions(names);
    } catch (e) {
      console.warn('[CreateLook] score compute failed:', e?.message);
    }
  };

  // ⚠️ STABLE (useCallback) : passé à AppModal comme onRequestClose. Sur web,
  // AppModal pousse une entrée history et son effet a `close` en dépendance ;
  // un handleClose inline (nouvelle réf à chaque render — et CreateLook re-render
  // beaucoup pendant le chargement du catalogue) re-déclenche l'effet → history.back()
  // → le modal se FERME TOUT SEUL. reset() n'appelle que des setters (stables).
  const handleClose = useCallback(() => {
    reset();
    onClose && onClose();
  }, [onClose]); // eslint-disable-line react-hooks/exhaustive-deps

  // Helper : filtre strict garantissant image visible + lien
  const isValidSuggestion = (p) => {
    if (!p || !p.id) return false;
    const img = p.image || p.image_url || '';
    if (!img || typeof img !== 'string') return false;
    if (!img.startsWith('http')) return false;
    if (!(p.link || p.affiliateUrl || p.url)) return false;
    if (!(p.price > 0)) return false;
    return true;
  };

  // Helper : variante autorisant aussi les items wardrobe (pas de link/price requis)
  // Utilisé en mode 'mix' pour ne pas filtrer les items du dressing comme suggestions.
  const isValidSuggestionOrWardrobe = (p) => {
    if (!p || !p.id) return false;
    if (p._source === 'wardrobe') {
      // wardrobe : juste une image suffit
      const img = p.image_url || p.image || '';
      return !!(img && typeof img === 'string' && img.startsWith('http'));
    }
    return isValidSuggestion(p);
  };

  // v1.12.501 — Helper : map backend response → grouped UI format + dedup global
  const mapBackendToGrouped = (backendRes, alreadySelected = []) => {
    // v1.12.508 — Dedup par image (photoUrl) en plus de l'id pour éviter variantes
    // (catalogue DDB a des productIds différents avec MÊME photoUrl)
    const getKey = (it) => String(it?.image_url || it?.image || it?.photoUrl || it?.id || it?.productId || '').trim();
    const globalSeenKeys = new Set(alreadySelected.map(getKey).filter(Boolean));
    return (backendRes?.missingCategories || [])
      .map(cat => {
        const items = (cat.suggestions || [])
          .map(s => {
            const it = s.item || {};
            const id = it.id || it.productId || it.pk || it.sku;
            const image = it.image || it.image_url || it.photoUrl;
            const link = it.link || it.affiliateUrl || it.url || it.purchaseUrl;
            return {
              ...it, id, image, image_url: image, link,
              _source: s.source,
              _score: s.score,
              _scoreBreakdown: s.scoreBreakdown,
              _feedback: s.feedback,
              _feedbackEn: s.feedbackEn,
              missingFor: cat.category,
            };
          })
          .filter(it => it.id && it.image)
          .filter(it => {
            const key = getKey(it);
            if (globalSeenKeys.has(key)) return false;
            globalSeenKeys.add(key);
            return true;
          });
        return {
          category: cat.category,
          reason: cat.reason || t('to_complete_your_look'),
          priority: cat.priority || 'medium',
          items,
        };
      })
      .filter(g => g.items.length > 0);
  };

  // v1.12.501 — Charge plus d'articles depuis le backend pour une catégorie (web only)
  // Doublons évités via dedup global dans mapBackendToGrouped
  const loadMoreForCategory = async (catName) => {
    if (loadingMoreCat) return;
    setLoadingMoreCat(catName);
    try {
      const newLimit = backendCatLimit + 6;
      setBackendCatLimit(newLimit);
      const OCCASION_TO_BACKEND = {
        'Casual': 'casual', 'Soirée': 'soiree', 'Bureau': 'travail',
        'Sport': 'sport', 'Cérémonie': 'ceremonie', 'Week-end': 'brunch',
        'Voyage': 'voyage', 'Rendez-vous': 'date',
      };
      const backendOccasion = OCCASION_TO_BACKEND[occasion] || 'casual';
      console.log('[CreateLook/LOAD_MORE] cat=' + catName + ' newLimit=' + newLimit);
      const res = await fetchCompleteLook({
        selectedItems, wardrobe: wardrobe || [], userProfile: null,
        context: { occasion: backendOccasion, tempC: 18 },
        gender: gender || 'F',
        sources: sourceMode === 'mix' ? ['wardrobe', 'catalogue'] : ['catalogue'],
        limitPerCategory: newLimit,
      });
      const newGrouped = mapBackendToGrouped(res, selectedItems);
      console.log('[CreateLook/LOAD_MORE] new sections:', newGrouped.length,
        'cat=' + catName + ' items:', newGrouped.find(g => g.category === catName)?.items?.length || 0);
      setGroupedSuggestions(newGrouped);
      setSuggestions(newGrouped.flatMap(g => g.items));
    } catch (e) {
      console.warn('[CreateLook/LOAD_MORE] failed:', e?.message || e);
    } finally {
      setLoadingMoreCat(null);
    }
  };

  // Étape 2 → 3 : générer suggestions IA (style LooksScreen avec grouping par manque)
  const generateSuggestions = async () => {
    if (!occasion || selectedItems.length === 0) {
      console.log('[CreateLook] generateSuggestions skipped (occasion=' + occasion + ' items=' + selectedItems.length + ')');
      return;
    }
    setLoading(true);
    try {
      const temperature = 18;
      const pseudoOutfit = { items: selectedItems, occasion, gender };

      // ═══ Pool de complétion selon le sourceMode ═══
      //  - wardrobe : suggest catalog completions (comportement legacy)
      //  - catalog  : suggest catalog completions also (pas de pollution wardrobe)
      //  - mix      : wardrobe + catalog, wardrobe taggé pour bonus +10
      // Note : le filtre isValidSuggestion ignore les items dressing (pas de link/price).
      // Pour le mode 'mix', on injecte les items wardrobe directement dans le pool
      // après les avoir taggés (le scorer trie ensuite avec le bonus).
      const richPool = richCatalog.length > 0 ? richCatalog : (catalog || []);
      const validCatalog = richPool.filter(isValidSuggestion);

      let suggestionPool;
      if (sourceMode === 'mix') {
        // Pour le mix : on ajoute les items wardrobe (NON sélectionnés) au pool
        // tagués _source=wardrobe ; le scorer leur donnera bonus +10.
        const selectedIds = new Set(selected);
        const wardrobeAsPool = (wardrobe || [])
          .filter(it => !selectedIds.has(it.id))
          .map(it => ({
            ...it,
            _source: 'wardrobe',
            // shims pour passer isValidSuggestion-like : on n'utilise PAS ce filtre sur wardrobe
            // mais downstream certaines fonctions peuvent regarder price/link → on laisse tel quel
          }));
        suggestionPool = [...wardrobeAsPool, ...validCatalog.map(it => ({ ...it, _source: 'catalog' }))];
      } else {
        // wardrobe ou catalog : seul le catalogue est utilisé pour compléter
        suggestionPool = validCatalog.map(it => ({ ...it, _source: 'catalog' }));
      }
      console.log('[CreateLook] sourceMode=' + sourceMode + ', pool size=' + suggestionPool.length + ' (valid catalog=' + validCatalog.length + ')');

      if (suggestionPool.length === 0) {
        Alert.alert(
          t('loading_in_progress'),
          t('catalog_still_loading')
        );
        setLoading(false);
        return;
      }

      // v1.12.498 — Map occasion FR maj → canonical lowercase backend
      const OCCASION_TO_BACKEND = {
        'Casual': 'casual', 'Soirée': 'soiree', 'Bureau': 'travail',
        'Sport': 'sport', 'Cérémonie': 'ceremonie', 'Week-end': 'brunch',
        'Voyage': 'voyage', 'Rendez-vous': 'date',
      };
      const backendOccasion = OCCASION_TO_BACKEND[occasion] || 'casual';

      // ═══ v1.12.497 — TRY BACKEND FIRST (POST /complete-look) ═══
      // Si réussi → on saute tout le calcul local et on convertit la réponse
      // backend (missingCategories) au format `grouped` attendu par l'UI.
      const wantBackendSources =
        sourceMode === 'mix' ? ['wardrobe', 'catalogue']
        : sourceMode === 'wardrobe' ? ['wardrobe', 'catalogue']  // wardrobe-only quasi inutile pour compléter
        : ['catalogue'];
      try {
        const _tBackend = Date.now();
        console.log('[CreateLook/BACKEND] REQUEST →', {
          selectedCount: selectedItems.length,
          selectedSample: selectedItems.slice(0, 3).map(i => ({
            id: i.id, category: i.category,
            formality: i?.ai_metadata?.formality,
            color: i?.ai_metadata?.primaryColor,
          })),
          wardrobeCount: (wardrobe || []).length,
          gender, occasion, backendOccasion, sources: wantBackendSources,
        });
        const backendRes = await fetchCompleteLook({
          selectedItems,
          wardrobe: wardrobe || [],
          userProfile: null,
          context: { occasion: backendOccasion, tempC: temperature },
          gender: gender || 'F',
          sources: wantBackendSources,
          limitPerCategory: 6,
        });
        const _bMs = Date.now() - _tBackend;
        console.log('[CreateLook/BACKEND] RESPONSE ←', {
          ms: _bMs,
          overallScore: backendRes?.overallScore,
          isComplete: backendRes?.isComplete,
          missingCategoriesCount: backendRes?.missingCategories?.length || 0,
          stats: backendRes?.stats,
          breakdown: (backendRes?.missingCategories || []).map(c => ({
            category: c.category,
            priority: c.priority,
            suggestionsCount: c.suggestions?.length || 0,
            top3: (c.suggestions || []).slice(0, 3).map(s => ({
              score: s.score,
              source: s.source,
              partner: s.item?.partner,
              color: s.item?.ai_metadata?.primaryColor,
              breakdown: s.scoreBreakdown,
            })),
          })),
        });

        // ═══ Convertir backend response → format `grouped` UI ═══
        // v1.12.500 — normaliser les champs DynamoDB → frontend
        // DynamoDB: { productId, photoUrl, affiliateUrl } → Frontend: { id, image, link }
        // v1.12.501 — dedup global (exclure selectedItems + doublons inter-catégories)
        const groupedFromBackend = mapBackendToGrouped(backendRes, selectedItems);

        if (groupedFromBackend.length > 0) {
          const flatBackend = groupedFromBackend.flatMap(g => g.items);
          console.log('[CreateLook/BACKEND] ✓ used backend:', groupedFromBackend.length, 'sections,', flatBackend.length, 'items');
          setGroupedSuggestions(groupedFromBackend);
          setSuggestions(flatBackend);
          setStep('preview');
          setLoading(false);
          return;
        }
        console.warn('[CreateLook/BACKEND] empty response → fallback local');
      } catch (e) {
        console.warn('[CreateLook/BACKEND] FAILED → fallback local:', e?.message || e);
      }

      // ═══ 1. Détection des catégories MANQUANTES (avis styliste pro) ═══ (LOCAL fallback)
      // Exemple si utilisateur a sélectionné Haut + Sac : proposera Bas + Chaussures + Veste
      const missing = detectMissingPieces(pseudoOutfit, temperature, occasion);
      console.log('[CreateLook/LOCAL] missing categories:', missing.map(m => m.category));

      // ═══ 2. Shuffle pool pour varier les résultats ═══
      const shuffled = [...suggestionPool];
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }

      // ═══ 3. Trouver suggestions (complete + replace via helper partagé) ═══
      //    - Catégories manquantes → propose pièces qui complètent le look
      //    - Items déjà présents → propose alternatives du catalogue (remplacement)
      const alreadySuggestedIds = new Set();
      // v1.12.272 — 8 propositions par catégorie + multiPerCategory pour vraiment retourner les 8
      const perCategory = 8;
      const allSuggs = getOutfitCatalogSuggestions(
        pseudoOutfit,
        shuffled,
        {
          tempC: temperature,
          occasion,
          gender: gender || 'F',
          maxPicks: 120, // on regroupera par catégorie ensuite (8 * ~15 catégories max)
          perCategory,
          alreadySuggestedIds,
          includeReplace: true,
          multiPerCategory: true,
        }
      );

      // ═══ 4. Construire la liste groupée par catégorie (pour UI sectionnée) ═══
      // Chaque suggestion a _isReplacement + _replacesLabel. On regroupe :
      //  - par catégorie missing (section "Haut manquant" etc.)
      //  - les remplacements vont dans une section "Alternatives" pour ne pas
      //    confondre avec ce qu'il manque réellement
      // ═══ DEDUP GLOBAL : un même article ne doit pas apparaître dans 2 sections ═══
      const grouped = [];
      const globalSeenIds = new Set();
      const replaceSuggs = allSuggs.filter(s => s._isReplacement);
      const completeSuggs = allSuggs.filter(s => !s._isReplacement);

      // Helper pour filtrer suggestions selon le sourceMode (mix accepte wardrobe items)
      const isValidForMode = sourceMode === 'mix' ? isValidSuggestionOrWardrobe : isValidSuggestion;

      // Sections pour catégories manquantes (priorité sur les alternatives)
      for (const miss of missing) {
        const items = completeSuggs
          .filter(s => s.category === miss.category)
          .filter(isValidForMode)
          .filter(s => {
            if (globalSeenIds.has(s.id)) return false;
            globalSeenIds.add(s.id);
            return true;
          });
        if (items.length === 0) continue;
        grouped.push({
          category: miss.category,
          reason: miss.reason,
          priority: miss.priority,
          items,
        });
      }

      // Section "Alternatives" (remplacements) en fin — exclut les déjà affichés
      if (replaceSuggs.length > 0) {
        const validReplaces = replaceSuggs
          .filter(isValidForMode)
          .filter(s => {
            if (globalSeenIds.has(s.id)) return false;
            globalSeenIds.add(s.id);
            return true;
          });
        if (validReplaces.length > 0) {
          grouped.push({
            category: 'Alternatives',
            reason: t('alternatives_to_your_items'),
            priority: 'low',
            items: validReplaces,
          });
        }
      }

      // Fallback : si aucune suggestion groupée, utiliser l'algo legacy
      if (grouped.length === 0 || grouped.every(g => g.items.length === 0)) {
        console.warn('[CreateLook] grouped empty, falling back to legacy');
        const legacy = suggestCatalogItems(pseudoOutfit, shuffled, 8);
        const byCat = {};
        // Dédup legacy aussi (même ID → 1 seule occurrence)
        const legacySeenIds = new Set();
        for (const item of legacy.filter(isValidForMode)) {
          if (legacySeenIds.has(item.id)) continue;
          legacySeenIds.add(item.id);
          const c = item.missingFor || item.category || 'Autres';
          if (!byCat[c]) byCat[c] = [];
          byCat[c].push(item);
        }
        for (const cat of Object.keys(byCat)) {
          grouped.push({
            category: cat,
            reason: t('to_complete_your_look'),
            priority: 'medium',
            items: byCat[cat],
          });
        }
      }

      // ═══ 5. Liste plate (pour compat + affichage) ═══
      const flat = grouped.flatMap(g => g.items);

      console.log('[CreateLook/LOCAL] final: ' + grouped.length + ' sections, ' + flat.length + ' items');
      setGroupedSuggestions(grouped);
      setSuggestions(flat);
      setStep('preview');
    } catch(e) {
      console.error('[CreateLook] suggestion error:', e.message, e.stack);
      // ═══ FALLBACK : au lieu de bloquer avec une alerte, on essaie le legacy ═══
      try {
        const pseudoOutfit = { items: selectedItems, occasion, gender };
        const primaryPool = richCatalog.length > 0 ? richCatalog : (catalog || []);
        const validCatalog = primaryPool.filter(isValidSuggestion);
        if (validCatalog.length > 0) {
          const legacy = suggestCatalogItems(pseudoOutfit, validCatalog, 12);
          const validLegacy = legacy.filter(isValidSuggestion);
          if (validLegacy.length > 0) {
            console.log('[CreateLook] fallback legacy:', validLegacy.length, 'items');
            const byCat = {};
            for (const item of validLegacy) {
              const c = item.missingFor || item.category || 'Autres';
              if (!byCat[c]) byCat[c] = [];
              byCat[c].push(item);
            }
            const grouped = Object.keys(byCat).map(cat => ({
              category: cat,
              reason: t('to_complete_your_look'),
              priority: 'medium',
              items: byCat[cat],
            }));
            setGroupedSuggestions(grouped);
            setSuggestions(validLegacy);
            setStep('preview');
            return;
          }
        }
      } catch (e2) {
        console.error('[CreateLook] legacy fallback also failed:', e2.message);
      }
      // Si même le fallback échoue, on affiche un message avec détail
      Alert.alert(
        t('error'),
        t('cannot_generate_suggestions') +
        (e.message ? '\n\n' + t('detail_label') + ' ' + e.message : '') +
        '\n\n' + t('try_restart_app')
      );
    } finally {
      setLoading(false);
    }
  };

  // Charger plus d'articles — même stratégie que LooksScreen
  const handleLoadMore = async () => {
    if (loadingMore) return;
    setLoadingMore(true);
    try {
      const { getZalandoProducts } = require('../services/ZalandoService');
      const { getAsosProducts }    = require('../services/AsosService');
      const { getZaraProducts }    = require('../services/ZaraService');
      const { getHMProducts }      = require('../services/HMService');

      // Rotation par catégorie + marque pour varier
      const CATS = ['Hauts', 'Bas', 'Robes', 'Vestes', 'Manteaux', 'Bijoux', 'Chaussures', 'Sacs', 'Accessoires'];
      const BRANDS = [getZalandoProducts, getAsosProducts, getZaraProducts, getHMProducts];
      const rotIdx = rotationRef.current;
      rotationRef.current = (rotIdx + 1) % (CATS.length * BRANDS.length);
      const cat   = CATS[rotIdx % CATS.length];
      const brand = BRANDS[rotIdx % BRANDS.length];

      console.log(`[CreateLook] loadMore: fetching ${cat} from brand ${rotIdx % BRANDS.length}`);
      const fetched = await brand(gender, cat, 30).catch(() => []);
      const newOnes = (fetched || []).filter(isValidSuggestion);

      // Exclure déjà suggérés + déjà dans catalogue (rich + externe + extra)
      const existingIds = new Set([
        ...suggestions.map(s => s.id),
        ...richCatalog.map(p => p.id),
        ...catalog.map(p => p.id),
        ...extraCatalog.map(p => p.id),
      ]);
      const fresh = newOnes.filter(p => !existingIds.has(p.id));
      console.log(`[CreateLook] loadMore: +${fresh.length} nouveaux articles (richCatalog=${richCatalog.length})`);

      if (fresh.length === 0) {
        // Fallback : re-suggérer avec le catalogue RICH existant + shuffle
        const allPool = [...richCatalog, ...(catalog || []), ...extraCatalog].filter(isValidSuggestion);
        const notYetSuggested = allPool.filter(p => !suggestions.some(s => s.id === p.id));
        const shuffled = [...notYetSuggested].sort(() => Math.random() - 0.5);
        const pseudoOutfit = { items: selectedItems, occasion, gender };
        const missing = detectMissingPieces(pseudoOutfit, 18, occasion);
        const aiSugg = findCatalogSuggestions(pseudoOutfit, missing, shuffled, 6, new Set());
        let more = Object.values(aiSugg).flat();
        if (more.length < 4) {
          const legacy = suggestCatalogItems(pseudoOutfit, shuffled, 6);
          more = more.concat(legacy.filter(x => !more.some(s => s.id === x.id)));
        }
        more = more.filter(isValidSuggestion).slice(0, 6);
        if (more.length > 0) {
          setSuggestions(prev => [...prev, ...more].slice(0, 20));
        }
        return;
      }

      // Étendre le catalogue extra + re-trouver suggestions avec les nouveaux
      setExtraCatalog(prev => [...prev, ...fresh]);
      const pseudoOutfit = { items: selectedItems, occasion, gender };
      const missing = detectMissingPieces(pseudoOutfit, 18, occasion);
      const aiSugg = findCatalogSuggestions(pseudoOutfit, missing, fresh, 3, new Set());

      // Fusionner par catégorie dans groupedSuggestions
      setGroupedSuggestions(prev => {
        const byCat = {};
        // Recréer map existante
        for (const g of prev) byCat[g.category] = [...g.items];

        // Ajouter les nouveaux articles par catégorie
        for (const cat of Object.keys(aiSugg)) {
          const newItems = (aiSugg[cat] || []).filter(isValidSuggestion);
          const existingIds = new Set((byCat[cat] || []).map(i => i.id));
          const addable = newItems.filter(n => !existingIds.has(n.id));
          byCat[cat] = [...(byCat[cat] || []), ...addable].slice(0, 10);
        }

        // Reconstruire la structure groupée
        const updated = [];
        for (const g of prev) {
          updated.push({ ...g, items: byCat[g.category] || g.items });
        }
        // Ajouter nouvelles catégories qui n'existaient pas
        for (const cat of Object.keys(byCat)) {
          if (!prev.find(g => g.category === cat)) {
            const missEntry = missing.find(m => m.category === cat);
            updated.push({
              category: cat,
              reason: missEntry?.reason || t('to_complete_your_look'),
              priority: missEntry?.priority || 'medium',
              items: byCat[cat],
            });
          }
        }
        return updated;
      });

      // Update flat list pour compat
      let extra = Object.values(aiSugg).flat().filter(isValidSuggestion);
      if (extra.length < 4) {
        const legacy = suggestCatalogItems(pseudoOutfit, fresh, 6);
        extra = extra.concat(legacy.filter(x => !extra.some(s => s.id === x.id)).filter(isValidSuggestion));
      }
      setSuggestions(prev => {
        const existing = new Set(prev.map(s => s.id));
        const addable = extra.filter(s => !existing.has(s.id));
        return [...prev, ...addable].slice(0, 30);
      });
    } catch(e) {
      console.warn('[CreateLook] loadMore error:', e.message);
    } finally {
      setLoadingMore(false);
    }
  };

  // v1.12.272 — Click sur une suggestion IA → ajoute/retire du look (avec swap si _replacesItemId)
  const handleSuggPress = (p) => {
    const isAdded = addedSuggestions.some(x => x.id === p.id);
    if (isAdded) {
      // Retirer la suggestion
      setAddedSuggestions(prev => prev.filter(x => x.id !== p.id));
      setSelected(prev => prev.filter(x => x !== p.id));
      return;
    }
    // Si replace : retirer aussi l'item remplacé
    if (p._isReplacement && p._replacesItemId) {
      setSelected(prev => prev.filter(x => x !== p._replacesItemId));
      // L'item remplacé peut être dans displayedItems (wardrobe ou catalog d'origine)
      // → setSelected gère, displayedItems le ré-évaluera
    }
    // Ajouter la suggestion à la sélection + au pool local des suggestions ajoutées
    const item = { ...p, _source: 'catalog' };
    setAddedSuggestions(prev => [...prev.filter(x => x.id !== p.id), item]);
    setSelected(prev => prev.includes(p.id) ? prev : [...prev, p.id]);
  };

  // Étape 3 → 4 : afficher score final puis demander le nom
  const validateLook = () => {
    computeScore();
    setStep('score');
  };

  // v1.12.282 — gate auth : pour sauvegarder un look il faut être connecté
  const [showSignInPrompt, setShowSignInPrompt] = useState(false);

  // Soumet le nom saisi : crée le look + ouvre next_actions
  const submitName = async () => {
    // v1.12.282 — bloque le save si user non authentifié
    if (!isAuthenticated) {
      setShowSignInPrompt(true);
      return;
    }
    const candidate = (lookName || '').trim();
    const v = validateLookName(candidate, existingLookNames);
    if (!v.valid) {
      const errMap = {
        name_required: t('name_error_required'),
        name_too_short: t('name_error_too_short'),
        name_too_long: t('name_error_too_long'),
        name_only_digits: t('name_error_only_digits'),
        name_forbidden_word: t('name_error_forbidden'),
        name_already_used: t('name_error_already_used'),
      };
      setNameError(errMap[v.error] || t('name_error_required'));
      return;
    }
    setNameError(null);

    const newLook = {
      id: 'look_' + Date.now(),
      name: candidate,
      occasion,
      items: selectedItems,
      suggestions,
      score: lookScore ? lookScore.final_score : null,
      grade: lookScore ? lookScore.grade : null,
      breakdown: lookScore ? lookScore.breakdown : null,
      createdAt: new Date().toISOString(),
    };
    setSavedLook(newLook);
    // v1.12.682 — GA4 : création d'un look · v1.12.894 — enrichi (partners, categories)
    try {
      const { trackEvent } = require('../services/AnalyticsService');
      const partners = new Set();
      const categories = new Set();
      for (const it of (Array.isArray(selectedItems) ? selectedItems : [])) {
        if (it?.partner) partners.add(String(it.partner));
        if (it?.category) categories.add(String(it.category));
      }
      const payload = {
        items_count: Array.isArray(selectedItems) ? selectedItems.length : 0,
        score: lookScore?.final_score ?? null,
        grade: lookScore?.grade ?? null,
        partners: Array.from(partners).join(',') || null,
        categories: Array.from(categories).join(',') || null,
        has_suggestions: Array.isArray(suggestions) && suggestions.length > 0,
      };
      // Event 1 : création (= les items sont choisis)
      trackEvent('look_created', payload);
      // Event 2 : sauvegarde explicite (alias pour funnel analytics save)
      trackEvent('look_saved', payload);
      // v1.13.63 — Event dédié "création de look sauvegardée" (nom explicite pour GA)
      trackEvent('create_look_saved', payload);
    } catch {}
    // v1.12.284 — await la persistance avant d'afficher la sheet, sinon "Voir mes looks"
    // peut arriver avant que AsyncStorage.setItem soit fini et la liste apparaît vide.
    if (onLookCreated) {
      try { await onLookCreated(newLook); } catch (e) { console.warn('[CreateLook] onLookCreated failed:', e?.message); }
    }
    // Notifie WardrobeScreen qu'un look a été ajouté (force le reload même si pas focus)
    try { require('react-native').DeviceEventEmitter.emit('wardrobe_look_added', { look: newLook }); } catch {}
    setNameDialog(false);
    setShowNextActions(true);
  };

  // v1.12.283 — gate try-on : auth (guest → sign in) + abonnement (free → upgrade)
  const [tryOnGate, setTryOnGate] = useState(null); // null | 'auth' | 'subscribe'

  // Handlers next_actions
  const handleNextTryOn = () => {
    // 1. Pas connecté → demande sign-in
    if (!isAuthenticated) { setTryOnGate('auth'); return; }
    // 2. Connecté mais non abonné → demande upgrade
    if (!canTryOn) { setTryOnGate('subscribe'); return; }
    // 3. OK → on lance try-on
    if (onTryOnLook && savedLook) onTryOnLook(savedLook);
    setShowNextActions(false);
    reset();
    onClose && onClose();
  };
  const handleNextCalendar = () => {
    if (onAddToCalendar && savedLook) onAddToCalendar(savedLook);
    setCalendarDone(true);
  };
  const handleNextShare = () => {
    if (onShareLook && savedLook) onShareLook(savedLook);
    else {
      try {
        const { Share } = require('react-native');
        Share.share({ message: `${savedLook?.name || 'Mon look'} — MyTenue` });
      } catch {}
    }
  };
  const handleNextBuy = () => {
    // Priorité 1 : suggestions catalogue (compléments à acheter)
    let firstCatalog = (suggestions || []).find(s => s.affiliateUrl || s.link || s.url);
    // Priorité 2 : items catalog déjà sélectionnés (mode catalog ou mix)
    if (!firstCatalog) {
      firstCatalog = (selectedItems || []).find(it => it._source === 'catalog' && (it.affiliateUrl || it.link || it.url));
    }
    if (firstCatalog && onProductPress) onProductPress(firstCatalog);
  };
  const closeAllAndExit = () => {
    setShowNextActions(false);
    reset();
    onClose && onClose();
  };

  if (!visible) return null;

  return (
    <AppModal visible transparent animationType="slide" onRequestClose={handleClose} webScrollable={false}>
      <View style={s.overlay}>
        <View style={s.card}>
          {/* Header */}
          <View style={s.header}>
            <View style={{ width: 36 }} />
            <View style={{ flex: 1, alignItems: 'center' }}>
              <Text style={s.headerBadge}>✨ {t('create_look_caps')}</Text>
              <Text style={s.headerTitle}>
                {step === 'select' ? t('step_choose_items')
                  : step === 'occasion' ? t('step_choose_occasion')
                  : step === 'preview' ? t('step_complete_look')
                  : t('final_score_title')}
              </Text>
            </View>
            <TouchableOpacity onPress={handleClose} style={s.closeBtn}>
              <Text style={s.closeText}>×</Text>
            </TouchableOpacity>
          </View>

          {/* Steps indicator */}
          <View style={s.stepsRow}>
            {['select','occasion','preview','score'].map((st, idx) => (
              <View key={st} style={[s.stepDot, step === st && s.stepDotActive,
                ['select','occasion','preview','score'].indexOf(step) > idx && s.stepDotDone]} />
            ))}
          </View>

          <ScrollView
            ref={mainScrollRef}
            style={{ flex: 1 }}
            contentContainerStyle={{ paddingBottom: 140 }}
            // v1.12.903 — Perf scroll Android : décroche les sous-vues hors viewport
            removeClippedSubviews={Platform.OS === 'android'}
            onScroll={({ nativeEvent }) => {
              // v1.12.456 — pagination scroll : charge +24 items (v1.12.903) près du bas
              if (activeTab !== 'catalog' || searchQ) return;
              const { layoutMeasurement, contentOffset, contentSize } = nativeEvent;
              const distanceFromBottom = contentSize.height - (layoutMeasurement.height + contentOffset.y);
              if (distanceFromBottom < 300 && displayedCount < (gridItems?.length || 0)) {
                setDisplayedCount((prev) => prev + CATALOG_PAGE_SIZE);
              }
            }}
            scrollEventThrottle={200}
          >
            {/* ═══ STEP 1 : Sélection articles ═══ */}
            {step === 'select' && (
              <View style={{ padding: spacing.md }}>
                <Text style={s.stepHelp}>
                  {t('select_1_to_4_intro')} <Text style={{ fontWeight: '700' }}>{t('select_1_to_4_emphasis')}</Text> {t('select_1_to_4_outro')}
                </Text>

                {/* ═══ v1.12.271 — Mes articles sélectionnés (toujours en haut, cumul wardrobe+catalog) ═══ */}
                <View style={s.selectionTopBar}>
                  <View style={s.selectionTopHeader}>
                    <Text style={s.selectionTopTitle}>{t('your_selection_header')}</Text>
                    <Text style={s.selectionTopCount}>
                      {selected.length}{selected.length > 0 ? ' · ' + (selected.length > 1 ? t('items_selected_plural') : t('item_selected_singular')) : ''}
                    </Text>
                  </View>
                  {selectedItems.length === 0 ? (
                    <View style={s.selectionTopEmpty}>
                      <Text style={s.selectionTopEmptyText}>{t('your_selection_empty')}</Text>
                    </View>
                  ) : (
                    <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ gap: 8, paddingVertical: 4 }}>
                      {selectedItems.map(item => {
                        const isCat = item._source === 'catalog';
                        return (
                          <TouchableOpacity
                            key={item.id}
                            onPress={() => toggleItem(item.id)}
                            style={s.selectionChip}
                            activeOpacity={0.85}
                          >
                            <SmartImage uri={item.image_url || item.image} style={s.selectionChipImg} />
                            <View style={[s.selectionChipBadge, isCat && s.sourceBadgeCatalog]}>
                              <Text style={s.selectionChipBadgeText}>{isCat ? '🛍️' : '👗'}</Text>
                            </View>
                            <View style={s.selectionChipRemove}>
                              <Text style={s.selectionChipRemoveText}>×</Text>
                            </View>
                          </TouchableOpacity>
                        );
                      })}
                    </ScrollView>
                  )}
                </View>

                {/* ═══ Tabs : Dressing / Catalogue / Favoris ═══ */}
                <View style={s.tabsRow}>
                  {[
                    { id: 'wardrobe',  labelKey: 'tab_wardrobe' },
                    { id: 'catalog',   labelKey: 'tab_catalog' },
                    { id: 'favorites', labelKey: 'tab_favorites' },
                  ].map(tab => {
                    const active = activeTab === tab.id;
                    return (
                      <TouchableOpacity
                        key={tab.id}
                        onPress={() => handleTabChange(tab.id)}
                        style={[s.tabBtn, active && s.tabBtnActive]}
                        activeOpacity={0.85}
                      >
                        <Text style={[s.tabBtnText, active && s.tabBtnTextActive]}>{t(tab.labelKey)}</Text>
                      </TouchableOpacity>
                    );
                  })}
                </View>

                {gridItems.length === 0 ? (
                  <View style={s.emptyBox}>
                    <Text style={s.emptyIcon}>
                      {activeTab === 'catalog' ? '🛍️' : activeTab === 'favorites' ? '❤️' : '👗'}
                    </Text>
                    <Text style={s.emptyTitle}>
                      {activeTab === 'wardrobe' ? t('wardrobe_empty')
                        : activeTab === 'favorites' ? t('favorites_empty_title')
                        : (loadingCatalog ? t('loading_catalog') : t('empty_catalog_mode'))}
                    </Text>
                    <Text style={s.emptyDesc}>
                      {activeTab === 'wardrobe' ? t('add_items_first')
                        : activeTab === 'favorites' ? t('favorites_empty_desc')
                        : t('catalog_still_loading')}
                    </Text>
                    {/* v1.12.309 — bouton CTA "Ajouter au dressing" si tab=wardrobe et vide */}
                    {activeTab === 'wardrobe' && onAddToWardrobe && (
                      <TouchableOpacity
                        onPress={() => { onAddToWardrobe(); onClose && onClose(); }}
                        style={s.emptyAddBtn}
                        activeOpacity={0.85}
                      >
                        <Text style={s.emptyAddBtnIcon}>＋</Text>
                        <Text style={s.emptyAddBtnText}>{t('add_to_wardrobe_cta')}</Text>
                      </TouchableOpacity>
                    )}
                  </View>
                ) : (
                  <>
                    {/* v1.12.219 — Barre de recherche + filtre catégorie */}
                    <View style={s.searchBar}>
                      <Text style={s.searchIcon}>🔍</Text>
                      <TextInput
                        value={searchQ}
                        onChangeText={setSearchQ}
                        placeholder={activeTab === 'catalog' ? t('search_catalog_placeholder') : t('search_by_name_brand_pseudo')}
                        placeholderTextColor="#A89E94"
                        style={s.searchInput}
                        returnKeyType="search"
                      />
                      {searchQ.length > 0 && (
                        <TouchableOpacity onPress={() => setSearchQ('')} hitSlop={{top:8,right:8,bottom:8,left:8}}>
                          <Text style={s.searchClear}>✕</Text>
                        </TouchableOpacity>
                      )}
                    </View>
                    {/* v1.12.312 — sur web : flex-wrap (tous les chips visibles) ; sur mobile : scroll horizontal */}
                    {Platform.OS === 'web' ? (
                      <View style={s.catChipsWrap}>
                        {availableCats.map(cat => {
                          const active = filterCat === cat;
                          const label = cat === 'Tout' ? t('all_categories') : translateCat(cat, t) || cat;
                          return (
                            <TouchableOpacity
                              key={cat}
                              onPress={() => setFilterCat(cat)}
                              style={[s.catChip, active && s.catChipActive]}
                              activeOpacity={0.8}
                            >
                              <Text style={[s.catChipText, active && s.catChipTextActive]}>{label}</Text>
                            </TouchableOpacity>
                          );
                        })}
                      </View>
                    ) : (
                      <ScrollView
                        horizontal
                        showsHorizontalScrollIndicator={false}
                        contentContainerStyle={s.catChipsRow}
                      >
                        {availableCats.map(cat => {
                          const active = filterCat === cat;
                          const label = cat === 'Tout' ? t('all_categories') : translateCat(cat, t) || cat;
                          return (
                            <TouchableOpacity
                              key={cat}
                              onPress={() => setFilterCat(cat)}
                              style={[s.catChip, active && s.catChipActive]}
                              activeOpacity={0.8}
                            >
                              <Text style={[s.catChipText, active && s.catChipTextActive]}>{label}</Text>
                            </TouchableOpacity>
                          );
                        })}
                      </ScrollView>
                    )}
                    {/* v1.12.312 — Filtre partenaire (onglet Catalogue uniquement) */}
                    {activeTab === 'catalog' && availablePartners.length > 1 && (
                      Platform.OS === 'web' ? (
                        <View style={[s.catChipsWrap, { marginTop: 6 }]}>
                          {availablePartners.map(p => {
                            const active = filterPartner === p;
                            const label = p === 'Tous' ? t('all_partners') || 'Tous' : p;
                            return (
                              <TouchableOpacity
                                key={p}
                                onPress={() => setFilterPartner(p)}
                                style={[s.partnerChip, active && s.partnerChipActive]}
                                activeOpacity={0.8}
                              >
                                <Text style={[s.partnerChipText, active && s.partnerChipTextActive]}>{label}</Text>
                              </TouchableOpacity>
                            );
                          })}
                        </View>
                      ) : (
                        <ScrollView
                          horizontal
                          showsHorizontalScrollIndicator={false}
                          contentContainerStyle={[s.catChipsRow, { marginTop: 6 }]}
                        >
                          {availablePartners.map(p => {
                            const active = filterPartner === p;
                            const label = p === 'Tous' ? t('all_partners') || 'Tous' : p;
                            return (
                              <TouchableOpacity
                                key={p}
                                onPress={() => setFilterPartner(p)}
                                style={[s.partnerChip, active && s.partnerChipActive]}
                                activeOpacity={0.8}
                              >
                                <Text style={[s.partnerChipText, active && s.partnerChipTextActive]}>{label}</Text>
                              </TouchableOpacity>
                            );
                          })}
                        </ScrollView>
                      )
                    )}
                    <Text style={s.resultsCount}>
                      {filteredItems.length} {filteredItems.length > 1 ? t('items_word_plural') : t('item_word_singular')}
                      {(searchQ || filterCat !== 'Tout') ? ' · ' + t('filtered_suffix') : ''}
                      {/* v1.12.456 — pagination scroll : affiche le compteur affiché/total */}
                      {activeTab === 'catalog' && !searchQ && filterCat === 'Tout' && gridItems.length > displayedCount
                        ? ` / ${gridItems.length}`
                        : ''}
                    </Text>
                    {filteredItems.length === 0 ? (
                      <View style={s.emptyBox}>
                        <Text style={s.emptyIcon}>
                          {activeTab === 'wardrobe' ? '👗' : activeTab === 'favorites' ? '❤️' : '🛍️'}
                        </Text>
                        <Text style={s.emptyTitle}>
                          {activeTab === 'catalog' ? t('empty_catalog_mode')
                            : activeTab === 'favorites' ? t('favorites_empty_title')
                            : (filterCat !== 'Tout' ? t('empty_wardrobe_mode') : t('no_results'))}
                        </Text>
                        <Text style={s.emptyDesc}>{t('change_search_or_category')}</Text>
                      </View>
                    ) : (
                      <View style={s.grid}>
                        {filteredItems.map(item => (
                          <GridCard
                            key={item.id}
                            item={item}
                            isSel={selected.includes(item.id)}
                            onPress={() => toggleItem(item.id)}
                            onLongPress={() => setZoomItem(item)}
                            translateCat={translateCat}
                            t={t}
                            s={s}
                          />
                        ))}
                      </View>
                    )}
                    {/* v1.12.458 — Bouton "Voir plus" fallback (scroll auto pas toujours fiable sur RN web) */}
                    {activeTab === 'catalog' && !searchQ && gridItems.length > displayedCount && (
                      <TouchableOpacity
                        onPress={() => setDisplayedCount((p) => p + CATALOG_PAGE_SIZE)}
                        style={{
                          alignSelf: 'center',
                          marginTop: 12,
                          paddingHorizontal: 24,
                          paddingVertical: 10,
                          backgroundColor: colors.text.primary,
                          borderRadius: radius.pill,
                        }}
                        activeOpacity={0.85}
                      >
                        <Text style={{ color: '#FFFFFF', fontWeight: '600', fontSize: 13, letterSpacing: 0.3 }}>
                          {/* v1.13.26 — libellé explicite (la clé i18n 'see_more' n'existait pas → affichait « see_more ») */}
                          {lang === 'en' ? 'See more' : 'Voir plus'} ({gridItems.length - displayedCount})
                        </Text>
                      </TouchableOpacity>
                    )}
                  </>
                )}
              </View>
            )}

            {/* ═══ STEP 2 : Occasion ═══ */}
            {step === 'occasion' && (
              <View style={{ padding: spacing.md }}>
                <Text style={s.stepHelp}>
                  {t('what_kind_of_look')}
                </Text>
                <View style={s.occasionsList}>
                  {OCCASIONS.map(occ => (
                    <TouchableOpacity
                      key={occ.id}
                      style={[s.occasionCard, occasion === occ.id && s.occasionCardActive]}
                      onPress={() => setOccasion(occ.id)}
                      activeOpacity={0.85}
                    >
                      <Text style={s.occasionIcon}>{occ.icon}</Text>
                      <View style={{ flex: 1 }}>
                        <Text style={[s.occasionLabel, occasion === occ.id && s.occasionLabelActive]}>
                          {t(occ.labelKey)}
                        </Text>
                        <Text style={s.occasionDesc}>{t(occ.descKey)}</Text>
                      </View>
                      {occasion === occ.id && <Text style={s.occasionCheck}>✓</Text>}
                    </TouchableOpacity>
                  ))}
                </View>
              </View>
            )}

            {/* ═══ STEP 3 : Preview + suggestions ═══ */}
            {step === 'preview' && (
              <View style={{ padding: spacing.md }}>
                {/* v1.12.504 — Badge occasion sélectionnée en haut */}
                {occasion && (() => {
                  const occMeta = OCCASIONS.find(o => o.id === occasion);
                  return (
                    <View style={s.occasionBadgeRow}>
                      <View style={s.occasionBadgePill}>
                        <Text style={s.occasionBadgeIcon}>{occMeta?.icon || '✨'}</Text>
                        <Text style={s.occasionBadgeText}>
                          {occMeta ? t(occMeta.labelKey) : occasion}
                        </Text>
                      </View>
                      <TouchableOpacity onPress={() => setStep('occasion')} style={s.occasionBadgeEdit}>
                        <Text style={s.occasionBadgeEditText}>{t('edit') || 'Modifier'}</Text>
                      </TouchableOpacity>
                    </View>
                  );
                })()}
                <Text style={s.stepHelp}>
                  {selectedItems.length === 1
                    ? t('ai_created_complete_look')
                    : t('your_items_plus_ai')}
                </Text>

                {/* Articles sélectionnés (dressing et/ou catalogue selon sourceMode) */}
                <View style={s.sectionBlock}>
                  <Text style={s.sectionTitle}>
                    {sourceMode === 'catalog' ? '🛍️' : sourceMode === 'mix' ? '✨' : '👗'} {t('your_items')}
                  </Text>
                  <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ gap: 10 }}>
                    {selectedItems.map(item => {
                      const isCat = item._source === 'catalog';
                      return (
                        <View key={item.id} style={s.itemCard}>
                          <View style={{ position: 'relative' }}>
                            <SmartImage uri={item.image_url || item.image} style={s.itemImg} />
                            <View style={[s.sourceBadge, isCat && s.sourceBadgeCatalog]}>
                              <Text style={s.sourceBadgeText}>{isCat ? '🛍️' : '👗'}</Text>
                            </View>
                            {/* v1.12.273 — bouton × pour retirer l'article du look */}
                            <TouchableOpacity
                              onPress={() => removeItem(item.id)}
                              hitSlop={{ top: 8, right: 8, bottom: 8, left: 8 }}
                              style={s.itemRemoveBtn}
                              activeOpacity={0.7}
                            >
                              <Text style={s.itemRemoveBtnText}>×</Text>
                            </TouchableOpacity>
                          </View>
                          <Text style={s.itemLabel} numberOfLines={1}>{item.brand || item.name}</Text>
                          <Text style={s.itemCat}>{translateCat(item.category, t)}</Text>
                        </View>
                      );
                    })}
                  </ScrollView>
                </View>

                {/* Suggestions pour compléter — GROUPÉES par catégorie manquante */}
                {groupedSuggestions.length > 0 && (() => {
                  // BEST MATCH = item suggestion avec _stylistScore le plus haut
                  let bestId = null; let bestScore = -1;
                  for (const g of groupedSuggestions) {
                    for (const it of g.items || []) {
                      const sc = +(it._stylistScore || 0);
                      if (sc > bestScore) { bestScore = sc; bestId = it.id; }
                    }
                  }
                  return (
                  <View style={s.sectionBlock}>
                    <Text style={s.sectionTitle}>
                      🛍️ {t('ai_suggestions_to_complete')}
                    </Text>
                    <Text style={s.aiStylistTip}>
                      {t('stylist_advice_intro')}
                    </Text>
                    <Text style={s.aiPicksHint}>
                      {t('ai_picks_per_category')}
                    </Text>

                    {groupedSuggestions.map((group, gIdx) => {
                      const CAT_EMOJI = {
                        Hauts: '👚', Bas: '👖', Robes: '👗', Vestes: '🧥',
                        Chaussures: '👟', Sacs: '👜', Bijoux: '💍', Accessoires: '✨',
                      };
                      const isPriorityHigh = group.priority === 'high';
                      return (
                        <View key={group.category + '_' + gIdx} style={s.categorySection}>
                          <View style={s.categorySectionHeader}>
                            <Text style={s.categorySectionEmoji}>{CAT_EMOJI[group.category] || '🛍️'}</Text>
                            <View style={{ flex: 1 }}>
                              <Text style={[s.categorySectionTitle, isPriorityHigh && { color: '#B8860B' }]}>
                                {group.category === 'Alternatives' ? t('alternatives') : translateCat(group.category, t) || group.category}
                                {isPriorityHigh && <Text style={s.categoryPriorityTag}> · {t('essential_tag')}</Text>}
                              </Text>
                              <Text style={s.categorySectionReason}>{group.reason}</Text>
                            </View>
                            {/* v1.12.506 — Bouton "Voir plus" web dans le header de section (toujours visible) */}
                            {Platform.OS === 'web' && group.category !== 'Alternatives' && (
                              <TouchableOpacity
                                onPress={() => loadMoreForCategory(group.category)}
                                disabled={loadingMoreCat === group.category}
                                activeOpacity={0.7}
                                style={s.headerMoreBtn}
                              >
                                {loadingMoreCat === group.category ? (
                                  <ActivityIndicator color="#8B6F2E" size="small" />
                                ) : (
                                  <Text style={s.headerMoreBtnText}>{t('more') || 'Plus'} →</Text>
                                )}
                              </TouchableOpacity>
                            )}
                          </View>
                          {/* v1.12.506 — Web: wrap multi-lignes (toutes les cards visibles). Mobile: scroll horizontal */}
                          {Platform.OS === 'web' ? (
                          <View style={s.suggGridWeb}>
                            {group.items.map(p => {
                              const isBest = p.id === bestId && bestScore >= 70;
                              const isReplace = !!p._isReplacement;
                              const isMatch = !isBest && !isReplace && (p._stylistScore || 0) >= 75;
                              const isAdded = selected.includes(p.id);
                              return (
                                <TouchableOpacity
                                  key={p.id}
                                  style={[s.suggCard, isBest && s.suggCardBest, isAdded && s.suggCardAdded]}
                                  onPress={() => handleSuggPress(p)}
                                  activeOpacity={0.85}
                                >
                                  <SmartImage uri={p.image || p.image_url} style={s.suggImg} />
                                  {isBest ? (
                                    <View style={s.bestMatchBadge}>
                                      <Text style={s.bestMatchBadgeText}>{t('badge_best_match')}</Text>
                                    </View>
                                  ) : isReplace ? (
                                    <View style={s.replaceBadge}>
                                      <Text style={s.replaceBadgeText}>{t('badge_replace')}</Text>
                                    </View>
                                  ) : isMatch ? (
                                    <View style={s.matchBadge}>
                                      <Text style={s.matchBadgeText}>{t('badge_match')}</Text>
                                    </View>
                                  ) : null}
                                  {isAdded && (
                                    <View style={s.suggCheckBadge}>
                                      <Text style={s.suggCheckBadgeText}>✓</Text>
                                    </View>
                                  )}
                                  <Text style={s.suggBrand} numberOfLines={1}>{p.brand || p.partner}</Text>
                                  <Text style={s.suggName} numberOfLines={2}>{p.name}</Text>
                                  <Text style={s.suggPrice}>{(p.price || 0).toFixed(0)}€</Text>
                                </TouchableOpacity>
                              );
                            })}
                          </View>
                          ) : (
                          <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ gap: 10 }}>
                            {group.items.map(p => {
                              const isBest = p.id === bestId && bestScore >= 70;
                              const isReplace = !!p._isReplacement;
                              const isMatch = !isBest && !isReplace && (p._stylistScore || 0) >= 75;
                              const isAdded = selected.includes(p.id);
                              return (
                                <TouchableOpacity
                                  key={p.id}
                                  style={[s.suggCard, isBest && s.suggCardBest, isAdded && s.suggCardAdded]}
                                  onPress={() => handleSuggPress(p)}
                                  activeOpacity={0.85}
                                >
                                  <SmartImage uri={p.image || p.image_url} style={s.suggImg} />
                                  {isBest ? (
                                    <View style={s.bestMatchBadge}>
                                      <Text style={s.bestMatchBadgeText}>{t('badge_best_match')}</Text>
                                    </View>
                                  ) : isReplace ? (
                                    <View style={s.replaceBadge}>
                                      <Text style={s.replaceBadgeText}>{t('badge_replace')}</Text>
                                    </View>
                                  ) : isMatch ? (
                                    <View style={s.matchBadge}>
                                      <Text style={s.matchBadgeText}>{t('badge_match')}</Text>
                                    </View>
                                  ) : null}
                                  {isAdded && (
                                    <View style={s.suggCheckBadge}>
                                      <Text style={s.suggCheckBadgeText}>✓</Text>
                                    </View>
                                  )}
                                  <Text style={s.suggBrand} numberOfLines={1}>{p.brand || p.partner}</Text>
                                  <Text style={s.suggName} numberOfLines={2}>{p.name}</Text>
                                  <Text style={s.suggPrice}>{(p.price || 0).toFixed(0)}€</Text>
                                </TouchableOpacity>
                              );
                            })}
                          </ScrollView>
                          )}
                        </View>
                      );
                    })}
                  </View>
                  );
                })()}
              </View>
            )}

            {/* ═══ STEP 4 : Score final + breakdown ═══ */}
            {step === 'score' && !lookScore && (
              <View style={{ padding: spacing.md, alignItems: 'center' }}>
                <ActivityIndicator color="#D4AF72" />
              </View>
            )}
            {step === 'score' && lookScore && (() => {
              const { final_score, grade, breakdown } = lookScore;
              const { strengths, weaknesses } = extractStrengthsWeaknesses(lookScore);
              const order = ['style', 'color', 'material', 'ai_vision', 'context'];
              return (
                <View style={{ padding: spacing.md }}>
                  {/* v1.12.505 — Mon look (vignettes des items sélectionnés) en haut */}
                  {selectedItems.length > 0 && (
                    <View style={s.sectionBlock}>
                      <Text style={s.sectionTitle}>
                        ✨ {t('your_look') || 'Mon look'} ({selectedItems.length})
                      </Text>
                      <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ gap: 10 }}>
                        {selectedItems.map(item => {
                          const isCat = item._source === 'catalog';
                          return (
                            <View key={'score_'+item.id} style={s.itemCard}>
                              <SmartImage uri={item.image_url || item.image} style={s.itemImg} />
                              <View style={[s.sourceBadge, isCat && s.sourceBadgeCatalog, { top: 4, left: 4 }]}>
                                <Text style={s.sourceBadgeText}>{isCat ? '🛍️' : '👗'}</Text>
                              </View>
                              <Text style={s.itemBrand} numberOfLines={1}>{item.brand || item.partner || ''}</Text>
                              <Text style={s.itemName} numberOfLines={2}>{item.name}</Text>
                              {!!item.price && <Text style={s.itemPrice}>{Number(item.price).toFixed(0)}€</Text>}
                            </View>
                          );
                        })}
                      </ScrollView>
                    </View>
                  )}

                  <Text style={s.stepHelp}>{t('final_score_subtitle')}</Text>

                  {/* Score circle + grade */}
                  <View style={s.scoreCircleWrap}>
                    <View style={[s.scoreCircle, final_score >= 80 ? s.scoreCircleHigh : final_score >= 60 ? s.scoreCircleMid : s.scoreCircleLow]}>
                      <Text style={s.scoreCircleNumber}>{final_score}</Text>
                      <Text style={s.scoreCircleMax}>/100</Text>
                    </View>
                    <View style={s.scoreGradeBox}>
                      <Text style={s.scoreGradeIcon}>{grade.icon}</Text>
                      <Text style={s.scoreGradeText}>
                        {grade.grade} · {t(grade.labelKey)}
                      </Text>
                    </View>
                  </View>

                  {/* v1.12.509 — Breakdown avec explanation + badge banks (backend) */}
                  <View style={s.breakdownBlock}>
                    {order.map(k => {
                      const b = breakdown[k];
                      if (!b) return null;
                      return (
                        <View key={k} style={s.breakdownRow}>
                          <Text style={s.breakdownIcon}>{b.icon}</Text>
                          <View style={{ flex: 1 }}>
                            <View style={s.breakdownLabelRow}>
                              <Text style={s.breakdownLabel}>{t(b.labelKey)}</Text>
                              <Text style={s.breakdownScore}>{b.score}/100</Text>
                            </View>
                            <View style={s.breakdownBarBg}>
                              <View style={[s.breakdownBarFill, { width: `${Math.min(100, b.score)}%`, backgroundColor: b.score >= 80 ? '#10B981' : b.score >= 60 ? '#D4AF72' : '#EF4444' }]} />
                            </View>
                          </View>
                        </View>
                      );
                    })}
                  </View>

                  {/* v1.12.509 — Context appliqué (température + occasion) */}
                  {lookScore._fromBackend && lookScore._backendContext && (
                    <View style={s.contextAppliedBlock}>
                      <Text style={s.contextAppliedText}>
                        🌡️ {t('temp_label') || 'Température'}: {lookScore._backendContext.tempC ?? '—'}°C · 🎭 {t('occasion_label') || 'Occasion'}: {occasion || lookScore._backendContext.occasion}
                        {' · '}
                        {lookScore._backendBanks && (
                          <Text style={{ color: '#8B6F2E' }}>
                            🏦 Banks: {[
                              lookScore._backendBanks.color && 'Couleur',
                              lookScore._backendBanks.material && 'Matière',
                              lookScore._backendBanks.style && 'Style',
                            ].filter(Boolean).join(' + ') || 'aucune'}
                          </Text>
                        )}
                      </Text>
                    </View>
                  )}

                  {/* ═══ Sources breakdown : dressing vs catalogue ═══ */}
                  {(() => {
                    const wCount = selectedItems.filter(it => it._source === 'wardrobe').length;
                    const cCount = selectedItems.filter(it => it._source === 'catalog').length;
                    if (wCount === 0 && cCount === 0) return null;
                    const tpl = t('sources_from') || '';
                    const line = tpl
                      .replace('{wardrobe_count}', String(wCount))
                      .replace('{catalog_count}', String(cCount));
                    return (
                      <View style={s.sourcesLine}>
                        <Text style={s.sourcesText}>{line}</Text>
                      </View>
                    );
                  })()}

                  {/* Strengths */}
                  {strengths.length > 0 && (
                    <View style={s.swBlock}>
                      <Text style={s.swTitle}>✅ {t('strengths_label')}</Text>
                      {strengths.map(sg => (
                        <Text key={sg.id} style={s.swItem}>
                          • {sg.name} · {sg.avg}/100
                        </Text>
                      ))}
                    </View>
                  )}

                  {/* v1.12.516 — Section "À améliorer" supprimée à la demande user */}

                  {/* ═══ Détails des conflits (v1.12.269) ═══ */}
                  {(() => {
                    const matIssues = breakdown?.material?.issues || [];
                    const colIssues = breakdown?.color?.issues || [];
                    const styIssues = breakdown?.style?.issues || [];
                    const matMissing = breakdown?.material?.missingData || [];
                    const colMissing = breakdown?.color?.missingData || [];
                    const styMissing = breakdown?.style?.missingData || [];
                    if (
                      matIssues.length === 0 && colIssues.length === 0 && styIssues.length === 0 &&
                      matMissing.length === 0 && colMissing.length === 0 && styMissing.length === 0
                    ) return null;

                    const famKey = (f) => {
                      if (!f) return '';
                      const k = `fam_${f}`;
                      const tr = t(k);
                      return tr === k ? f : tr;
                    };
                    const fill = (tpl, vars) => Object.entries(vars).reduce((s, [k, v]) => s.replaceAll(`{${k}}`, String(v ?? '')), tpl);
                    const uniqNames = (arr) => Array.from(new Set(arr.map(x => x.name))).join(', ');

                    return (
                      <View style={s.conflictsBlock}>
                        {matIssues.length > 0 && (
                          <View style={s.conflictGroup}>
                            <Text style={s.conflictGroupTitle}>🧵 {t('conflict_material_label')}</Text>
                            {matIssues.map((iss, idx) => {
                              const tpl = iss.aFam && iss.bFam && iss.aFam === iss.bFam
                                ? t('conflict_material_line_same_family')
                                : t('conflict_material_line');
                              const line = fill(tpl, {
                                a: iss.a, b: iss.b,
                                aMat: iss.aMat, bMat: iss.bMat,
                                aFam: famKey(iss.aFam), bFam: famKey(iss.bFam),
                              });
                              return (
                                <Text key={`m${idx}`} style={[s.conflictItem, iss.severity === 'high' && s.conflictItemHigh]}>
                                  • {line}
                                </Text>
                              );
                            })}
                          </View>
                        )}

                        {colIssues.length > 0 && (
                          <View style={s.conflictGroup}>
                            <Text style={s.conflictGroupTitle}>🎨 {t('conflict_color_label')}</Text>
                            {colIssues.map((iss, idx) => {
                              const line = fill(t('conflict_color_line'), {
                                a: iss.a, b: iss.b,
                                aCol: iss.aCol, bCol: iss.bCol,
                              });
                              return (
                                <Text key={`c${idx}`} style={[s.conflictItem, iss.severity === 'high' && s.conflictItemHigh]}>
                                  • {line}
                                </Text>
                              );
                            })}
                          </View>
                        )}

                        {styIssues.length > 0 && (
                          <View style={s.conflictGroup}>
                            <Text style={s.conflictGroupTitle}>🎯 {t('conflict_style_label')}</Text>
                            {styIssues.map((iss, idx) => {
                              const line = fill(t('conflict_style_line'), {
                                item: iss.item,
                                wanted: iss.wantedOccasion,
                                actual: iss.actualOccasion,
                                wantedScore: iss.wantedScore,
                                actualScore: iss.actualScore,
                              });
                              return (
                                <Text key={`s${idx}`} style={[s.conflictItem, iss.severity === 'high' && s.conflictItemHigh]}>
                                  • {line}
                                </Text>
                              );
                            })}
                          </View>
                        )}

                        {(matMissing.length > 0 || colMissing.length > 0 || styMissing.length > 0) && (
                          <View style={s.conflictMissingBox}>
                            {matMissing.length > 0 && (
                              <Text style={s.conflictMissing}>
                                ℹ️ {fill(t('conflict_missing_material'), { names: uniqNames(matMissing) })}
                              </Text>
                            )}
                            {colMissing.length > 0 && (
                              <Text style={s.conflictMissing}>
                                ℹ️ {fill(t('conflict_missing_color'), { names: uniqNames(colMissing) })}
                              </Text>
                            )}
                            {styMissing.length > 0 && (
                              <Text style={s.conflictMissing}>
                                ℹ️ {fill(t('conflict_missing_occasion'), { names: uniqNames(styMissing) })}
                              </Text>
                            )}
                          </View>
                        )}
                      </View>
                    );
                  })()}
                </View>
              );
            })()}
          </ScrollView>

          {/* Bottom CTA */}
          <View style={[s.bottom, { paddingBottom: Math.max(16, insets.bottom + 8) }]}>
            {step === 'select' && (
              <View>
                {/* v1.12.519 — Notification user si aucun article sélectionné */}
                {selected.length === 0 && (
                  <View style={s.noticeBox}>
                    <Text style={s.noticeIcon}>ℹ️</Text>
                    <Text style={s.noticeText}>{t('select_at_least_one_item') || 'Sélectionnez au moins un article pour continuer'}</Text>
                  </View>
                )}
                <TouchableOpacity
                  style={[s.ctaBtn, selected.length === 0 && s.ctaBtnDisabled]}
                  disabled={selected.length === 0}
                  onPress={() => { try { require('../services/AnalyticsService').trackTap('ui_createlook_next', { from: 'select', items: selected.length }); } catch {} setStep('occasion'); }}
                  activeOpacity={0.85}
                >
                  <Text style={[s.ctaBtnText, selected.length === 0 && s.ctaBtnTextDisabled]}>
                    {t('next_arrow')}
                  </Text>
                </TouchableOpacity>
              </View>
            )}
            {step === 'occasion' && (
              <View>
                {/* v1.12.519 — Notification si l'user a désélectionné tous les items entre temps */}
                {selectedItems.length === 0 && (
                  <View style={s.noticeBox}>
                    <Text style={s.noticeIcon}>ℹ️</Text>
                    <Text style={s.noticeText}>{t('select_at_least_one_item') || 'Sélectionnez au moins un article pour continuer'}</Text>
                  </View>
                )}
                <View style={{ flexDirection: 'row', gap: 10 }}>
                <TouchableOpacity style={s.ctaSecondary} onPress={() => setStep('select')} activeOpacity={0.85}>
                  <Text style={s.ctaSecondaryText}>{t('back_arrow')}</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[s.ctaBtn, { flex: 1 }, (!occasion || loading || loadingCatalog || selectedItems.length === 0) && s.ctaBtnDisabled]}
                  disabled={!occasion || loading || loadingCatalog || selectedItems.length === 0}
                  onPress={() => { try { require('../services/AnalyticsService').trackTap('ui_createlook_generate', { occasion, items: selectedItems.length }); } catch {} generateSuggestions(); }}
                  activeOpacity={0.85}
                >
                  {(loading || loadingCatalog)
                    ? (
                      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                        <ActivityIndicator color="#D4AF72" />
                        <Text style={[s.ctaBtnText, { fontSize: 11 }]}>
                          {loadingCatalog ? t('loading_catalog') : t('loading_generation')}
                        </Text>
                      </View>
                    )
                    : <Text style={[s.ctaBtnText, !occasion && s.ctaBtnTextDisabled]}>{t('generate_look')}</Text>
                  }
                </TouchableOpacity>
                </View>
              </View>
            )}
            {step === 'preview' && (
              <View style={{ flexDirection: 'row', gap: 10 }}>
                <TouchableOpacity style={s.ctaSecondary} onPress={() => setStep('occasion')} activeOpacity={0.85}>
                  <Text style={s.ctaSecondaryText}>{t('back_arrow')}</Text>
                </TouchableOpacity>
                <TouchableOpacity style={[s.ctaBtn, { flex: 1 }]} onPress={() => { try { require('../services/AnalyticsService').trackTap('ui_createlook_validate', { items: selectedItems.length }); } catch {} validateLook(); }} activeOpacity={0.85}>
                  <Text style={s.ctaBtnText}>{t('save_this_look_check')}</Text>
                </TouchableOpacity>
              </View>
            )}
            {step === 'score' && (
              <View style={{ flexDirection: 'row', gap: 10 }}>
                <TouchableOpacity style={s.ctaSecondary} onPress={() => setStep('preview')} activeOpacity={0.85}>
                  <Text style={s.ctaSecondaryText}>{t('back_arrow')}</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[s.ctaBtn, { flex: 1 }]}
                  onPress={() => {
                    try { require('../services/AnalyticsService').trackTap('ui_createlook_save_open', { score: lookScore?.global ?? null }); } catch {}
                    // Pré-remplir le nom avec la 1re suggestion
                    if (!lookName && nameSuggestions[0]) setLookName(nameSuggestions[0]);
                    setNameError(null);
                    setNameDialog(true);
                  }}
                  activeOpacity={0.85}
                >
                  <Text style={s.ctaBtnText}>{t('save_look_action')}</Text>
                </TouchableOpacity>
              </View>
            )}
          </View>

          {/* ═══ Dialog : saisie du nom du look ═══ */}
          {nameDialog && (
            <View style={s.nameDialogOverlay}>
              <View style={s.nameDialogBox}>
                <Text style={s.nameDialogTitle}>{t('enter_look_name')}</Text>

                <Text style={s.nameDialogSubtitle}>{t('suggest_names_label')}</Text>
                <View style={s.suggestionsList}>
                  {nameSuggestions.map((n, i) => (
                    <TouchableOpacity
                      key={i}
                      style={[s.suggestionChip, lookName === n && s.suggestionChipActive]}
                      onPress={() => { setLookName(n); setNameError(null); }}
                      activeOpacity={0.85}
                    >
                      <Text style={[s.suggestionChipText, lookName === n && s.suggestionChipTextActive]}>{n}</Text>
                    </TouchableOpacity>
                  ))}
                </View>

                <TextInput
                  value={lookName}
                  onChangeText={(v) => { setLookName(v); setNameError(null); }}
                  placeholder={t('enter_look_name')}
                  placeholderTextColor="#A89E94"
                  style={s.nameInput}
                  maxLength={50}
                />
                {nameError && <Text style={s.nameErrorText}>{nameError}</Text>}

                <View style={{ flexDirection: 'row', gap: 10, marginTop: 10 }}>
                  <TouchableOpacity style={s.ctaSecondary} onPress={() => setNameDialog(false)} activeOpacity={0.85}>
                    <Text style={s.ctaSecondaryText}>{t('cancel')}</Text>
                  </TouchableOpacity>
                  <TouchableOpacity style={[s.ctaBtn, { flex: 1 }]} onPress={submitName} activeOpacity={0.85}>
                    <Text style={s.ctaBtnText}>{t('save_look_action')}</Text>
                  </TouchableOpacity>
                </View>
              </View>
            </View>
          )}

          {/* ═══ next_actions sheet ═══ */}
          {showNextActions && (
            <View style={s.nameDialogOverlay}>
              <View style={s.nextActionsBox}>
                {/* v1.12.282 — banner confirmation enregistrement */}
                <View style={s.savedBanner}>
                  <Text style={s.savedBannerTitle}>{t('look_saved_banner_title')}</Text>
                  <Text style={s.savedBannerBody}>{t('look_saved_banner_body')}</Text>
                </View>

                {savedLook?.name ? (
                  <Text style={s.nextActionsLookName}>{savedLook.name}</Text>
                ) : null}

                <Text style={s.nameDialogTitle}>{t('next_actions_title')}</Text>

                {/* v1.12.282 — bouton Voir mes looks (redirige vers Garde-robe > Mes Looks) */}
                {onViewMyLooks && (
                  <TouchableOpacity
                    style={s.nextActionPrimary}
                    onPress={() => { setShowNextActions(false); reset(); onClose && onClose(); onViewMyLooks(); }}
                    activeOpacity={0.85}
                  >
                    <Text style={s.nextActionPrimaryText}>{t('next_action_view_my_looks')}</Text>
                  </TouchableOpacity>
                )}

                {/* Primary : Try-on */}
                <TouchableOpacity style={s.nextActionPrimary} onPress={handleNextTryOn} activeOpacity={0.85}>
                  <Text style={s.nextActionPrimaryText}>{t('next_action_tryon')}</Text>
                </TouchableOpacity>

                <TouchableOpacity
                  style={[s.nextActionSecondary, calendarDone && { backgroundColor: '#ECFDF5' }]}
                  onPress={handleNextCalendar}
                  activeOpacity={0.85}
                  disabled={calendarDone}
                >
                  <Text style={s.nextActionSecondaryText}>
                    {calendarDone ? '✓ ' : ''}{t('next_action_calendar')}
                  </Text>
                </TouchableOpacity>

                <TouchableOpacity style={s.nextActionSecondary} onPress={handleNextShare} activeOpacity={0.85}>
                  <Text style={s.nextActionSecondaryText}>{t('next_action_share')}</Text>
                </TouchableOpacity>

                {/* Buy button : visible si suggestions catalog OU si selected contient des items catalog
                    (mode pure-wardrobe → tout owned → pas de buy) */}
                {(((suggestions || []).some(s2 => s2.affiliateUrl || s2.link || s2.url))
                  || (selectedItems || []).some(it => it._source === 'catalog')) && (
                  <TouchableOpacity style={s.nextActionSecondary} onPress={handleNextBuy} activeOpacity={0.85}>
                    <Text style={s.nextActionSecondaryText}>{t('next_action_buy')}</Text>
                  </TouchableOpacity>
                )}

                <TouchableOpacity style={[s.ctaSecondary, { marginTop: 12, alignSelf: 'stretch', alignItems: 'center' }]} onPress={closeAllAndExit} activeOpacity={0.85}>
                  <Text style={s.ctaSecondaryText}>{t('close')}</Text>
                </TouchableOpacity>
              </View>
            </View>
          )}

          {/* v1.12.283 — Try-on gate (auth + abonnement) */}
          {tryOnGate && (
            <View style={s.nameDialogOverlay}>
              <View style={s.nextActionsBox}>
                <Text style={{ fontSize: 36, textAlign: 'center', marginBottom: 8 }}>
                  {tryOnGate === 'auth' ? '🔒' : '✨'}
                </Text>
                <Text style={[s.nameDialogTitle, { textAlign: 'center' }]}>
                  {tryOnGate === 'auth' ? t('tryon_signin_title') : t('tryon_subscribe_title')}
                </Text>
                <Text style={{ fontSize: 13, color: '#666', lineHeight: 18, textAlign: 'center', marginBottom: 12 }}>
                  {tryOnGate === 'auth' ? t('tryon_signin_body') : t('tryon_subscribe_body')}
                </Text>
                <TouchableOpacity
                  style={s.nextActionPrimary}
                  onPress={() => {
                    const wasAuth = tryOnGate === 'auth';
                    setTryOnGate(null);
                    setShowNextActions(false);
                    reset();
                    onClose && onClose();
                    if (wasAuth) onRequestSignIn && onRequestSignIn();
                    else onRequestSubscribe && onRequestSubscribe();
                  }}
                  activeOpacity={0.85}
                >
                  <Text style={s.nextActionPrimaryText}>
                    {tryOnGate === 'auth' ? t('require_auth_cta') : t('tryon_subscribe_cta')}
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[s.ctaSecondary, { marginTop: 8, alignSelf: 'stretch', alignItems: 'center' }]}
                  onPress={() => setTryOnGate(null)}
                  activeOpacity={0.85}
                >
                  <Text style={s.ctaSecondaryText}>{t('close')}</Text>
                </TouchableOpacity>
              </View>
            </View>
          )}

          {/* v1.12.282 — Sign-in prompt : bloque le save pour les guests */}
          {showSignInPrompt && (
            <View style={s.nameDialogOverlay}>
              <View style={s.nextActionsBox}>
                <Text style={{ fontSize: 36, textAlign: 'center', marginBottom: 8 }}>🔒</Text>
                <Text style={[s.nameDialogTitle, { textAlign: 'center' }]}>
                  {t('sign_in_to_save_look_title')}
                </Text>
                <Text style={{ fontSize: 13, color: '#666', lineHeight: 18, textAlign: 'center', marginBottom: 12 }}>
                  {t('sign_in_to_save_look_body')}
                </Text>
                <TouchableOpacity
                  style={s.nextActionPrimary}
                  onPress={() => {
                    setShowSignInPrompt(false);
                    reset();
                    onClose && onClose();
                    onRequestSignIn && onRequestSignIn();
                  }}
                  activeOpacity={0.85}
                >
                  <Text style={s.nextActionPrimaryText}>{t('require_auth_cta')}</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[s.ctaSecondary, { marginTop: 8, alignSelf: 'stretch', alignItems: 'center' }]}
                  onPress={() => setShowSignInPrompt(false)}
                  activeOpacity={0.85}
                >
                  <Text style={s.ctaSecondaryText}>{t('close')}</Text>
                </TouchableOpacity>
              </View>
            </View>
          )}
        </View>
      </View>

      {/* v1.13.x — Aperçu image en grand (long-press sur un article). Overlay
          absolu DANS le modal (pas un modal imbriqué → évite le bug history web). */}
      {zoomItem && (
        <TouchableOpacity
          style={s.zoomOverlay}
          activeOpacity={1}
          onPress={() => setZoomItem(null)}
        >
          <SmartImage
            uri={zoomItem.image_url || zoomItem.image}
            style={s.zoomImg}
            resizeMode="contain"
          />
          <Text style={s.zoomName} numberOfLines={2}>
            {zoomItem.brand || zoomItem.name || ''}
          </Text>
          <Text style={s.zoomHint}>
            {(lang && lang.startsWith('en')) ? 'Tap to close' : 'Touchez pour fermer'}
          </Text>
        </TouchableOpacity>
      )}
    </AppModal>
  );
}

const s = StyleSheet.create({
  // v1.13.x — Overlay aperçu image en grand (long-press)
  zoomOverlay: {
    position: 'absolute',
    top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.92)',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    padding: 24,
  },
  zoomImg: { width: '90%', height: '70%', borderRadius: 12 },
  zoomName: { color: '#FFF', fontSize: 16, fontWeight: '700', marginTop: 18, textAlign: 'center' },
  zoomHint: { color: 'rgba(255,255,255,0.6)', fontSize: 13, marginTop: 8 },
  overlay: {
    ...(Platform.OS === 'web'
      ? { width: '100%', height: Dimensions.get('window').height, alignItems: 'center', justifyContent: 'flex-end' }
      : { flex: 1, backgroundColor: 'transparent', justifyContent: 'flex-end' }
    ),
  },
  card: {
    ...(Platform.OS === 'web'
      // v1.12.270 — CTA bas figée : on donne une hauteur explicite à la card
      // pour que le ScrollView interne respecte la limite et que la barre
      // bottom (position:absolute) reste collée au bas du viewport.
      ? { width: '100%', maxWidth: 720, height: Dimensions.get('window').height - 60 }
      : { flex: 1, marginTop: 60 }
    ),
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: 28, borderTopRightRadius: 28,
    overflow: 'hidden',
    position: 'relative',
  },

  header: {
    flexDirection: 'row', alignItems: 'center',
    paddingHorizontal: 16, paddingTop: 16, paddingBottom: 10,
    backgroundColor: '#FAFAFA',
    borderBottomWidth: 0.5, borderBottomColor: '#E8E0C8',
  },
  closeBtn: { width: 36, height: 36, borderRadius: 18, alignItems: 'center', justifyContent: 'center', backgroundColor: '#F5F5F5' },
  closeText: { fontSize: 20, color: '#666', lineHeight: 22 },
  headerBadge: { fontSize: 9, letterSpacing: 2.5, color: '#B8860B', fontWeight: '700', marginBottom: 2 },
  headerTitle: { fontSize: 16, fontWeight: '300', color: '#1A1A1A', letterSpacing: 0.3 },

  stepsRow: {
    flexDirection: 'row', justifyContent: 'center', gap: 6,
    paddingVertical: 8, backgroundColor: '#FAFAFA',
    borderBottomWidth: 0.5, borderBottomColor: '#E8E0C8',
  },
  stepDot: { width: 24, height: 3, borderRadius: 1.5, backgroundColor: '#E0E0E0' },
  stepDotActive: { backgroundColor: '#1A1A1A' },
  stepDotDone: { backgroundColor: '#B8860B' },

  stepHelp: { fontSize: 13, color: '#555', marginBottom: 12, lineHeight: 18 },

  selectedBar: {
    backgroundColor: '#FFF9E6',
    borderWidth: 1, borderColor: '#F5DEB3',
    borderRadius: 10,
    paddingHorizontal: 14, paddingVertical: 10,
    marginBottom: 12,
  },
  selectedBarText: { fontSize: 12, color: '#B8860B', fontWeight: '700', letterSpacing: 0.5 },
  selectedBarHint: { fontSize: 11, color: '#666', marginTop: 4, fontStyle: 'italic' },

  emptyBox: { alignItems: 'center', padding: 40, gap: 8 },
  emptyIcon: { fontSize: 48 },
  emptyTitle: { fontSize: 14, fontWeight: '600' },
  emptyDesc: { fontSize: 12, color: '#999', textAlign: 'center' },
  // v1.12.309 — bouton CTA "Ajouter au dressing" dans empty state wardrobe tab
  emptyAddBtn: {
    marginTop: 12,
    flexDirection: 'row', alignItems: 'center', gap: 8,
    paddingHorizontal: 18, paddingVertical: 12,
    backgroundColor: '#1A1A1A',
    borderRadius: 100,
  },
  emptyAddBtnIcon: { fontSize: 18, color: '#D4AF72', fontWeight: '700' },
  emptyAddBtnText: { fontSize: 12, color: '#D4AF72', fontWeight: '700', letterSpacing: 1 },

  // v1.12.219 — recherche + filtre catégorie
  searchBar: {
    flexDirection: 'row', alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    borderWidth: 1, borderColor: '#EAE6E0',
    paddingHorizontal: 12, height: 42,
    marginBottom: 10,
    gap: 8,
  },
  searchIcon: { fontSize: 14, opacity: 0.6 },
  searchInput: {
    flex: 1, fontSize: 14, color: '#1A1A1A',
    paddingVertical: 0,
    ...(typeof window !== 'undefined' && window.document ? { outlineWidth: 0 } : {}),
  },
  searchClear: { fontSize: 14, color: '#A89E94', paddingHorizontal: 4 },
  catChipsRow: { gap: 8, paddingBottom: 4, paddingRight: 8 },
  catChipsWrap: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, paddingBottom: 4 },
  catChip: {
    paddingHorizontal: 14, paddingVertical: 7,
    borderRadius: 16,
    backgroundColor: '#FFFFFF',
    borderWidth: 1, borderColor: '#EAE6E0',
  },
  catChipActive: {
    backgroundColor: '#FFF7E0',
    borderColor: '#D4AF72',
  },
  catChipText: { fontSize: 12, color: '#7A7268', fontWeight: '500' },
  catChipTextActive: { color: '#B8860B', fontWeight: '700' },
  // v1.12.312 — chips partenaire (un peu plus discrètes, palette neutre)
  partnerChip: {
    paddingHorizontal: 12, paddingVertical: 6,
    borderRadius: 14,
    backgroundColor: '#FAF8F5',
    borderWidth: 1, borderColor: '#E8E3DC',
  },
  partnerChipActive: {
    backgroundColor: '#1A1A1A',
    borderColor: '#1A1A1A',
  },
  partnerChipText: { fontSize: 11, color: '#7A7268', fontWeight: '600', letterSpacing: 0.3 },
  partnerChipTextActive: { color: '#FFFFFF', fontWeight: '700' },
  resultsCount: { fontSize: 11, color: '#A89E94', marginTop: 8, marginBottom: 6, fontStyle: 'italic' },

  // ═══ Source mode selector (Dressing / Catalogue / Mix) ═══
  sourceModeLabel: {
    fontSize: 10, fontWeight: '700', color: '#A89E94',
    letterSpacing: 1.2, textTransform: 'uppercase',
    marginBottom: 8, marginTop: 4,
  },
  sourceModeRow: {
    flexDirection: 'row', gap: 8, marginBottom: 14,
  },
  sourceModeChip: {
    flex: 1,
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    gap: 6,
    paddingVertical: 10, paddingHorizontal: 8,
    borderRadius: 100,
    backgroundColor: '#FFFFFF',
    borderWidth: 1.5, borderColor: '#EAE6E0',
  },
  sourceModeChipActive: {
    backgroundColor: '#FFF7E0',
    borderColor: '#D4AF72',
  },
  sourceModeIcon: { fontSize: 14 },
  sourceModeText: { fontSize: 11, color: '#7A7268', fontWeight: '600' },
  sourceModeTextActive: { color: '#B8860B', fontWeight: '700' },

  // ═══ v1.12.271 — Selection top bar (cumulée wardrobe+catalogue) ═══
  selectionTopBar: {
    backgroundColor: '#FFFBF5',
    borderWidth: 1, borderColor: '#EAE6E0',
    borderRadius: 12,
    paddingHorizontal: 10, paddingVertical: 8,
    marginBottom: 12,
  },
  selectionTopHeader: {
    flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
    marginBottom: 6,
  },
  selectionTopTitle: { fontSize: 11, fontWeight: '700', color: '#B8860B', letterSpacing: 0.5, textTransform: 'uppercase' },
  selectionTopCount: { fontSize: 10, color: '#7A7268', fontWeight: '600' },
  selectionTopEmpty: { paddingVertical: 8, paddingHorizontal: 4 },
  selectionTopEmptyText: { fontSize: 11, color: '#A89E94', fontStyle: 'italic', textAlign: 'center' },
  selectionChip: {
    width: 64, position: 'relative',
  },
  selectionChipImg: {
    width: 64, height: 64, borderRadius: 8,
    backgroundColor: '#F5F0E8',
  },
  selectionChipBadge: {
    position: 'absolute', top: 2, left: 2,
    width: 18, height: 18, borderRadius: 9,
    backgroundColor: 'rgba(255,255,255,0.92)',
    alignItems: 'center', justifyContent: 'center',
    borderWidth: 1, borderColor: '#D4AF72',
  },
  selectionChipBadgeText: { fontSize: 9 },
  selectionChipRemove: {
    position: 'absolute', top: -4, right: -4,
    width: 18, height: 18, borderRadius: 9,
    backgroundColor: '#1A1A1A',
    alignItems: 'center', justifyContent: 'center',
  },
  selectionChipRemoveText: { color: '#FFF', fontSize: 12, fontWeight: '700', lineHeight: 14 },

  // ═══ v1.12.271 — Tabs Dressing / Catalogue ═══
  tabsRow: {
    flexDirection: 'row', gap: 0,
    borderBottomWidth: 1, borderBottomColor: '#EAE6E0',
    marginBottom: 12,
  },
  tabBtn: {
    flex: 1,
    paddingVertical: 12, alignItems: 'center',
    borderBottomWidth: 2, borderBottomColor: 'transparent',
    marginBottom: -1,
  },
  tabBtnActive: { borderBottomColor: '#D4AF72' },
  tabBtnText: { fontSize: 13, color: '#7A7268', fontWeight: '600' },
  tabBtnTextActive: { color: '#1A1A1A', fontWeight: '700' },

  // Source badge sur la grille (top-left)
  sourceBadge: {
    position: 'absolute', top: 6, left: 6,
    width: 22, height: 22, borderRadius: 11,
    backgroundColor: 'rgba(255,255,255,0.92)',
    alignItems: 'center', justifyContent: 'center',
    borderWidth: 1, borderColor: '#D4AF72',
    zIndex: 2,
  },
  sourceBadgeCatalog: { borderColor: '#1A1A1A' },
  sourceBadgeText: { fontSize: 11 },

  grid: { flexDirection: 'row', flexWrap: 'wrap', gap: 10 },
  gridCard: {
    width: '31%',
    backgroundColor: '#fff',
    borderRadius: 12, overflow: 'hidden',
    borderWidth: 2, borderColor: '#EEE',
    position: 'relative',
  },
  gridCardSelected: { borderColor: '#B8860B', borderWidth: 2 },
  gridImg: { width: '100%', height: 110 },
  // v1.13.27 — signal "appui long = agrandir" (badge loupe en bas-gauche)
  zoomHintBadge: {
    position: 'absolute', bottom: 30, left: 6,
    width: 20, height: 20, borderRadius: 10,
    backgroundColor: 'rgba(0,0,0,0.5)', alignItems: 'center', justifyContent: 'center',
  },
  zoomHintIcon: { color: '#FFFFFF', fontSize: 11, fontWeight: '700' },
  checkBadge: {
    position: 'absolute', top: 6, right: 6,
    width: 24, height: 24, borderRadius: 12,
    backgroundColor: '#B8860B',
    alignItems: 'center', justifyContent: 'center',
  },
  checkBadgeText: { color: '#fff', fontSize: 14, fontWeight: '700' },
  gridName: { fontSize: 11, fontWeight: '600', paddingHorizontal: 6, paddingTop: 4 },
  gridCat: { fontSize: 9, color: '#999', paddingHorizontal: 6, paddingBottom: 6 },

  occasionsList: { gap: 10 },
  occasionCard: {
    flexDirection: 'row', alignItems: 'center', gap: 14,
    padding: 14,
    backgroundColor: '#fff',
    borderWidth: 1.5, borderColor: '#EEE',
    borderRadius: 14,
  },
  occasionCardActive: { borderColor: '#B8860B', backgroundColor: '#FFF9E6' },
  occasionIcon: { fontSize: 30 },
  occasionLabel: { fontSize: 15, fontWeight: '600', color: '#1A1A1A', marginBottom: 2 },
  occasionLabelActive: { color: '#B8860B' },
  occasionDesc: { fontSize: 11, color: '#999' },
  occasionCheck: { fontSize: 20, color: '#B8860B', fontWeight: '700' },

  sectionBlock: { marginBottom: 20 },
  sectionTitle: { fontSize: 13, fontWeight: '600', color: '#1A1A1A', marginBottom: 10 },

  itemCard: { width: 90, alignItems: 'center' },
  itemImg: { width: 90, height: 110, borderRadius: 8, backgroundColor: '#F5F5F5' },
  // v1.12.273 — bouton × sur les cards selected items
  itemRemoveBtn: {
    position: 'absolute', top: -6, right: -6,
    width: 22, height: 22, borderRadius: 11,
    backgroundColor: '#1A1A1A',
    alignItems: 'center', justifyContent: 'center',
    borderWidth: 2, borderColor: '#FFF',
    zIndex: 5,
  },
  itemRemoveBtnText: { color: '#FFF', fontSize: 14, fontWeight: '800', lineHeight: 16 },
  itemLabel: { fontSize: 10, fontWeight: '600', marginTop: 4, textAlign: 'center' },
  itemCat: { fontSize: 9, color: '#999' },

  // v1.12.506 — Web : grille wrap multi-lignes pour voir toutes les suggestions
  suggGridWeb: {
    flexDirection: 'row', flexWrap: 'wrap', gap: 10,
  },
  // v1.12.506 — Bouton "Voir plus" dans le header de section (web only)
  headerMoreBtn: {
    backgroundColor: '#FFF8E7', borderColor: '#D4AF72', borderWidth: 1,
    paddingHorizontal: 12, paddingVertical: 6, borderRadius: 12,
  },
  headerMoreBtnText: { fontSize: 12, fontWeight: '600', color: '#8B6F2E' },
  suggCard: {
    width: 130,
    backgroundColor: '#fff',
    borderWidth: 1, borderColor: '#F5DEB3',
    borderRadius: 10,
    padding: 6,
    position: 'relative',
  },
  // v1.12.509 — Badge "Bank IA appliquée" à côté du label breakdown
  banksAppliedBadge: {
    backgroundColor: '#FFF8E7', borderColor: '#D4AF72', borderWidth: 1,
    paddingHorizontal: 6, paddingVertical: 1, borderRadius: 8, marginLeft: 6,
  },
  banksAppliedText: { fontSize: 9, color: '#8B6F2E', fontWeight: '600' },
  // v1.12.509 — Explanation sous chaque barre breakdown
  breakdownExplanation: {
    fontSize: 11, color: '#6B7280', marginTop: 4, lineHeight: 14, fontStyle: 'italic',
  },
  // v1.12.509 — Block contexte appliqué (temp + occasion + banks)
  contextAppliedBlock: {
    backgroundColor: '#FAF7F2', borderLeftWidth: 3, borderLeftColor: '#D4AF72',
    paddingHorizontal: 12, paddingVertical: 8, borderRadius: 6, marginVertical: 10,
  },
  contextAppliedText: { fontSize: 12, color: '#5C4F2E', lineHeight: 17 },
  // v1.12.509 — Issues backend avec fix suggestions
  backendIssueRow: { flexDirection: 'row', alignItems: 'flex-start', gap: 8, marginVertical: 6 },
  backendIssueSev: { fontSize: 14, marginTop: 1 },
  backendIssueText: { fontSize: 13, color: '#374151', lineHeight: 17 },
  backendIssueFix: {
    flexDirection: 'row', alignItems: 'flex-start', gap: 5,
    backgroundColor: '#FFFBEB', borderLeftWidth: 2, borderLeftColor: '#F59E0B',
    paddingHorizontal: 8, paddingVertical: 5, borderRadius: 4, marginTop: 4,
  },
  backendIssueFixIcon: { fontSize: 12 },
  backendIssueFixText: { flex: 1, fontSize: 12, color: '#92400E', lineHeight: 15 },
  // v1.12.519 — Notice "Aucun article sélectionné" au-dessus du CTA bottom
  noticeBox: {
    flexDirection: 'row', alignItems: 'center', gap: 8,
    backgroundColor: '#FFF7E6', borderColor: '#F5C16C', borderWidth: 1,
    paddingHorizontal: 12, paddingVertical: 10, borderRadius: 10,
    marginBottom: 10,
  },
  noticeIcon: { fontSize: 16 },
  noticeText: { flex: 1, fontSize: 13, color: '#7A5A1E', lineHeight: 17 },
  // v1.12.504 — Badge occasion sélectionnée en haut de step preview
  occasionBadgeRow: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    marginBottom: 10, gap: 8,
  },
  occasionBadgePill: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    backgroundColor: '#FFF8E7', borderColor: '#D4AF72', borderWidth: 1,
    paddingHorizontal: 12, paddingVertical: 6, borderRadius: 16,
  },
  occasionBadgeIcon: { fontSize: 14 },
  occasionBadgeText: { fontSize: 13, fontWeight: '600', color: '#8B6F2E' },
  occasionBadgeEdit: { paddingHorizontal: 10, paddingVertical: 4 },
  occasionBadgeEditText: { fontSize: 12, color: '#666', textDecorationLine: 'underline' },
  // v1.12.501 — carte "Voir plus" (web only) à la fin du scroll horizontal de chaque catégorie
  loadMoreCard: {
    width: 80, height: 200,
    backgroundColor: '#FAF7F2',
    borderWidth: 1, borderColor: '#D4AF72',
    borderStyle: 'dashed',
    borderRadius: 10,
    alignItems: 'center', justifyContent: 'center',
    gap: 6,
  },
  loadMoreArrow: { fontSize: 28, color: '#D4AF72', fontWeight: '600' },
  loadMoreLabel: { fontSize: 11, color: '#8B6F2E', fontWeight: '500' },
  suggImg: { width: '100%', height: 130, borderRadius: 6 },
  suggBrand: { fontSize: 9, color: '#999', letterSpacing: 1, textTransform: 'uppercase', marginTop: 4 },
  suggName: { fontSize: 10, fontWeight: '500', color: '#1A1A1A', marginTop: 2, lineHeight: 13 },
  suggPrice: { fontSize: 12, color: '#B8860B', fontWeight: '700', marginTop: 4 },

  matchBadge: {
    position: 'absolute', top: 8, left: 8,
    backgroundColor: '#10B981',
    paddingHorizontal: 6, paddingVertical: 2,
    borderRadius: 4,
  },
  matchBadgeText: { fontSize: 8, color: '#fff', fontWeight: '700', letterSpacing: 0.5 },
  // v1.12.271 — badge Replace (alternative à un item déjà sélectionné)
  replaceBadge: {
    position: 'absolute', top: 8, left: 8,
    backgroundColor: '#6366F1',
    paddingHorizontal: 6, paddingVertical: 2,
    borderRadius: 4,
  },
  replaceBadgeText: { fontSize: 8, color: '#fff', fontWeight: '700', letterSpacing: 0.5 },
  aiPicksHint: {
    fontSize: 10, color: '#7A7268', fontStyle: 'italic',
    marginTop: 4, marginBottom: 8,
  },

  aiStylistTip: {
    fontSize: 11, color: '#B8860B',
    backgroundColor: '#FFF9E6',
    borderLeftWidth: 3, borderLeftColor: '#D4AF37',
    paddingHorizontal: 12, paddingVertical: 10,
    marginBottom: 14,
    borderRadius: 6,
    lineHeight: 16,
  },

  categorySection: {
    marginBottom: 18,
  },
  categorySectionHeader: {
    flexDirection: 'row', alignItems: 'center', gap: 10,
    marginBottom: 8,
  },
  categorySectionEmoji: { fontSize: 24 },
  categorySectionTitle: {
    fontSize: 13, fontWeight: '700', color: '#1A1A1A', letterSpacing: 0.5,
  },
  categoryPriorityTag: {
    fontSize: 9, fontWeight: '700', color: '#B8860B', letterSpacing: 1,
  },
  categorySectionReason: {
    fontSize: 11, color: '#999', marginTop: 1,
  },

  loadMoreBtn: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    gap: 8,
    backgroundColor: '#FFF9E6',
    borderWidth: 1.5, borderColor: '#F5DEB3',
    borderStyle: 'dashed',
    borderRadius: 100,
    paddingVertical: 12, paddingHorizontal: 20,
    marginTop: 8,
  },
  loadMoreBtnText: {
    fontSize: 12, fontWeight: '700', color: '#B8860B', letterSpacing: 0.5,
  },

  loadMoreCard: {
    width: 130, height: 210,
    backgroundColor: '#FFF9E6',
    borderWidth: 1.5, borderColor: '#F5DEB3',
    borderStyle: 'dashed',
    borderRadius: 10,
    alignItems: 'center', justifyContent: 'center',
    gap: 8, padding: 10,
  },
  loadMoreIconWrap: {
    width: 40, height: 40, borderRadius: 20,
    backgroundColor: '#fff',
    borderWidth: 1, borderColor: '#F5DEB3',
    alignItems: 'center', justifyContent: 'center',
  },
  loadMoreIcon: { fontSize: 20 },
  loadMoreLabel: { fontSize: 11, fontWeight: '700', color: '#B8860B', letterSpacing: 0.3, textAlign: 'center' },
  loadMoreSub: { fontSize: 9, color: '#999', textAlign: 'center' },

  bottom: {
    // v1.12.270 — figé en bas de la card (donc du viewport sur web grâce à
    // la hauteur explicite ci-dessus, et du modal natif sinon)
    position: 'absolute',
    left: 0, right: 0, bottom: 0,
    padding: 16,
    borderTopWidth: 0.5, borderTopColor: '#E8E0C8',
    backgroundColor: '#FAFAFA',
    zIndex: 10,
  },
  ctaBtn: {
    backgroundColor: '#1A1A1A',
    paddingVertical: 14,
    borderRadius: 100,
    alignItems: 'center',
  },
  ctaBtnDisabled: { backgroundColor: '#E0E0E0' },
  ctaBtnText: { color: '#D4AF72', fontSize: 13, fontWeight: '700', letterSpacing: 1 },
  ctaBtnTextDisabled: { color: '#999' },
  ctaSecondary: {
    paddingVertical: 14, paddingHorizontal: 18,
    borderRadius: 100,
    borderWidth: 1, borderColor: '#DDD',
    backgroundColor: '#fff',
  },
  ctaSecondaryText: { fontSize: 12, color: '#666', fontWeight: '600' },

  // ═══ Option A v1.12.73 — score step, badges, dialog, next_actions ═══
  suggCardBest: { borderColor: '#D4AF37', borderWidth: 2, shadowColor: '#D4AF37', shadowOpacity: 0.4, shadowRadius: 6 },
  // v1.12.272 — card de suggestion ajoutée au look
  suggCardAdded: { borderColor: '#10B981', borderWidth: 2, backgroundColor: '#F0FDF4' },
  suggCheckBadge: {
    position: 'absolute', top: 8, right: 8,
    width: 22, height: 22, borderRadius: 11,
    backgroundColor: '#10B981',
    alignItems: 'center', justifyContent: 'center',
    borderWidth: 2, borderColor: '#FFF',
    zIndex: 3,
  },
  suggCheckBadgeText: { color: '#FFF', fontSize: 12, fontWeight: '800' },
  bestMatchBadge: {
    position: 'absolute', top: 8, left: 8,
    backgroundColor: '#1A1A1A',
    paddingHorizontal: 7, paddingVertical: 3,
    borderRadius: 4,
  },
  bestMatchBadgeText: { fontSize: 8, color: '#D4AF72', fontWeight: '800', letterSpacing: 0.8 },

  scoreCircleWrap: {
    flexDirection: 'row', alignItems: 'center', gap: 16,
    paddingVertical: 16, marginBottom: 14,
  },
  scoreCircle: {
    width: 110, height: 110, borderRadius: 55,
    alignItems: 'center', justifyContent: 'center',
    borderWidth: 4,
  },
  scoreCircleHigh: { borderColor: '#10B981', backgroundColor: '#ECFDF5' },
  scoreCircleMid:  { borderColor: '#D4AF72', backgroundColor: '#FFF9E6' },
  scoreCircleLow:  { borderColor: '#EF4444', backgroundColor: '#FEF2F2' },
  scoreCircleNumber: { fontSize: 36, fontWeight: '800', color: '#1A1A1A' },
  scoreCircleMax: { fontSize: 11, color: '#666', marginTop: -4 },
  scoreGradeBox: { flex: 1 },
  scoreGradeIcon: { fontSize: 28 },
  scoreGradeText: { fontSize: 14, fontWeight: '700', color: '#1A1A1A', marginTop: 4, letterSpacing: 0.4 },

  breakdownBlock: { gap: 12, marginBottom: 14 },
  breakdownRow: { flexDirection: 'row', alignItems: 'flex-start', gap: 10 },
  breakdownIcon: { fontSize: 18, width: 22, textAlign: 'center', marginTop: 2 },
  breakdownLabelRow: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4 },
  breakdownLabel: { fontSize: 12, fontWeight: '700', color: '#1A1A1A', letterSpacing: 0.3 },
  breakdownScore: { fontSize: 12, fontWeight: '600', color: '#666' },
  breakdownBarBg: { height: 6, borderRadius: 3, backgroundColor: '#F5F5F5', overflow: 'hidden' },
  breakdownBarFill: { height: '100%', borderRadius: 3 },
  breakdownWeight: { fontSize: 9, color: '#999', marginTop: 3, fontStyle: 'italic' },

  // ═══ Sources breakdown (score step) ═══
  sourcesLine: {
    backgroundColor: '#FAFAFA',
    borderWidth: 1, borderColor: '#EAE6E0',
    borderRadius: 8,
    paddingHorizontal: 12, paddingVertical: 8,
    marginBottom: 12,
  },
  sourcesText: { fontSize: 11, color: '#666', fontWeight: '500', letterSpacing: 0.3 },

  swBlock: {
    backgroundColor: '#F0FDF4',
    borderLeftWidth: 3, borderLeftColor: '#10B981',
    padding: 12, borderRadius: 6,
    marginBottom: 12,
  },
  swTitle: { fontSize: 12, fontWeight: '700', color: '#1A1A1A', marginBottom: 6 },
  swItem: { fontSize: 11, color: '#444', lineHeight: 16 },

  // ═══ Conflict details (v1.12.269) ═══
  conflictsBlock: { marginBottom: 12, gap: 8 },
  conflictGroup: {
    backgroundColor: '#FFFBF5',
    borderLeftWidth: 3, borderLeftColor: '#D97706',
    paddingHorizontal: 10, paddingVertical: 8,
    borderRadius: 6,
  },
  conflictGroupTitle: { fontSize: 11, fontWeight: '700', color: '#7C2D12', marginBottom: 4, letterSpacing: 0.3 },
  conflictItem: { fontSize: 11, color: '#444', lineHeight: 16, marginBottom: 2 },
  conflictItemHigh: { color: '#991B1B', fontWeight: '600' },
  conflictMissingBox: {
    backgroundColor: '#F9FAFB',
    borderWidth: 1, borderColor: '#E5E7EB',
    paddingHorizontal: 10, paddingVertical: 6,
    borderRadius: 6,
  },
  conflictMissing: { fontSize: 10, color: '#6B7280', lineHeight: 14, fontStyle: 'italic' },

  // Name dialog
  nameDialogOverlay: {
    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    alignItems: 'center', justifyContent: 'center',
    padding: 16,
  },
  nameDialogBox: {
    width: '100%', maxWidth: 480,
    backgroundColor: '#fff',
    borderRadius: 16,
    padding: 18,
  },
  nameDialogTitle: { fontSize: 16, fontWeight: '700', color: '#1A1A1A', marginBottom: 12, letterSpacing: 0.3 },
  nameDialogSubtitle: { fontSize: 11, color: '#999', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 1 },
  suggestionsList: { flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginBottom: 12 },
  suggestionChip: {
    paddingHorizontal: 10, paddingVertical: 6,
    borderRadius: 14,
    backgroundColor: '#FAFAFA',
    borderWidth: 1, borderColor: '#EAE6E0',
  },
  suggestionChipActive: { backgroundColor: '#FFF9E6', borderColor: '#D4AF72' },
  suggestionChipText: { fontSize: 11, color: '#666' },
  suggestionChipTextActive: { color: '#B8860B', fontWeight: '700' },
  nameInput: {
    borderWidth: 1, borderColor: '#EAE6E0',
    borderRadius: 10,
    paddingHorizontal: 12, paddingVertical: 10,
    fontSize: 14, color: '#1A1A1A',
    ...(typeof window !== 'undefined' && window.document ? { outlineWidth: 0 } : {}),
  },
  nameErrorText: { fontSize: 11, color: '#EF4444', marginTop: 6 },

  // Next actions sheet
  nextActionsBox: {
    width: '100%', maxWidth: 480,
    backgroundColor: '#fff',
    borderRadius: 16,
    padding: 18,
    gap: 8,
  },
  nextActionsLookName: { fontSize: 12, color: '#B8860B', fontWeight: '600', marginBottom: 8 },
  // v1.12.282 — banner confirmation enregistrement
  savedBanner: {
    backgroundColor: '#ECFDF5',
    borderLeftWidth: 4, borderLeftColor: '#10B981',
    paddingHorizontal: 14, paddingVertical: 10,
    borderRadius: 8,
    marginBottom: 6,
  },
  savedBannerTitle: { fontSize: 14, fontWeight: '700', color: '#065F46', marginBottom: 2 },
  savedBannerBody: { fontSize: 12, color: '#047857', lineHeight: 16 },
  nextActionPrimary: {
    backgroundColor: '#1A1A1A',
    paddingVertical: 16,
    borderRadius: 100,
    alignItems: 'center',
  },
  nextActionPrimaryText: { color: '#D4AF72', fontSize: 14, fontWeight: '700', letterSpacing: 1 },
  nextActionDone: {
    paddingVertical: 12,
    borderRadius: 100,
    alignItems: 'center',
    backgroundColor: '#ECFDF5',
    borderWidth: 1, borderColor: '#10B981',
  },
  nextActionDoneText: { color: '#065F46', fontSize: 12, fontWeight: '700' },
  nextActionSecondary: {
    paddingVertical: 12,
    borderRadius: 100,
    alignItems: 'center',
    backgroundColor: '#FAFAFA',
    borderWidth: 1, borderColor: '#EAE6E0',
  },
  nextActionSecondaryText: { fontSize: 12, color: '#1A1A1A', fontWeight: '600' },
});
